import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Center,
  Container,
  Divider,
  Flex,
  Group,
  Loader,
  Modal,
  MultiSelect,
  NumberInput,
  Paper,
  Select,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import {
  IconArrowLeft,
  IconBell,
  IconBriefcase,
  IconBuildingBank,
  IconCalendar,
  IconCalendarEvent,
  IconChartBar,
  IconCreditCard,
  IconReceipt,
  IconDatabase,
  IconDevices,
  IconEdit,
  IconEye,
  IconFileText,
  IconLock,
  IconMail,
  IconSettings,
  IconShield,
  IconUser,
  IconUsersGroup,
  IconClipboardList,
  IconWorld,
  IconLink,
  IconClock,
  IconInfoCircle,
  IconX,
  IconPlus,
  IconRotate,
} from '@tabler/icons-react';
import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ElementType,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { UnifyNavLink } from '../../components/UnifyNavLink';
import { useMediaQuery } from '@mantine/hooks';
import { UsersSectionPage } from '../users/UsersSectionPage';
import { UserSettings } from './components/UserSettings';
import { PracticeSettings } from './components/PracticeSettings';
import { AppointmentTypeAccessPanel } from './components/ManageAppointmentTypeAccessModal';
import { notifications } from '@mantine/notifications';
import {
  editMember,
  editOrgSettings,
  editOrgBrand,
  sendSystemStatusTestEmail,
} from '../../lib/adminActionsService';
import { patientToUser, practitionerToUser, practicesToUserMap } from '../../lib/adminDirectoryMapping';
import { loadFreshDirectoryUserFromProfile, PROFILE_RESOURCE_READ_OPTIONS, resolveDirectoryUserFromProfile } from '../../lib/medplumDirectoryUsers';
import { useMedplumDirectoryUsers } from '../../hooks/useMedplumDirectoryUsers';
import { useEffectiveDisplayUserRole } from '../../lib/displayUserRole';
import { usePermissions } from '../../contexts/PermissionContext';
import { canAccessAuditLogsUi } from '../audit-logs/auditLogsAccess';
import { AuditLogsPage } from '../audit-logs';
import { filterUsersForPractice, type CreateUserPayload } from '../../lib/adminDirectoryService';
import { triggerPasswordResetEmail, fetchAuthMeSnapshot, logoutAllDevices, type ActiveLoginSession } from '../../lib/securitySettingsService';
import {
  ensureAiToolsConsent,
  optInAiToolsConsent,
  optInSessionRecordingConsent,
  revokeAiToolsConsent,
  revokeSessionRecordingConsent,
  sessionRecordingZoomResyncWarningMessage,
} from '../../lib/portalFormSubmissionService';
import { loadAiToolsConsentState } from '../../lib/aiToolsConsentClient';
import { getAiToolsSwitchUiState } from '../../lib/aiToolsConsentSwitch';
import { loadSessionRecordingDecision } from '../../lib/sessionRecordingConsentClient';
import { getSessionRecordingSwitchUiState } from '../../lib/sessionRecordingConsentSwitch';
import { usePortalSubjectPatientId } from '../../hooks/usePortalSubjectPatientId';
import { SignatureCaptureModal } from '../../components/SignatureCapture';
import type { SignatureInfo } from '../appointments/fhirNoteMapping';
import { type AiToolsConsentDecision, type AiTranscriptionDecision, type SessionRecordingDecision } from '@unify/fhir-model/resources/consent';
import { TemplateLibrary } from './template-library';
import { NotificationTemplatesPage } from './notifications';
import type { Client } from '../caseload/types';
import { isRoleMembershipOnlyUpdate, type User, type Practice } from '../users/types/rbac';
import type { ContactPoint, HumanName, Patient, Practitioner, PractitionerRole } from '@medplum/fhirtypes';
import {
  getPractitionerDisplayTimeZone,
  setPractitionerDisplayTimeZone,
  stripPractitionerLegacyPreferredNames,
  upsertPractitionerNickname,
  upsertPractitionerOfficialName,
  splitDisplayNameToOfficialParts,
} from '@unify/fhir-model/resources/practitioner';
import { setPreferredDisplayTimeZone } from '@/lib/unifyDateFormat';
import {
  DEFAULT_AI_NOTE_GENERATION_SYSTEM_PROMPT,
  SESSION_DURATION_PRESETS,
  upsertAppointmentSessionType,
  upsertChargeItemDefinitionRow,
  upsertMeetingType,
  upsertTimeEntryType,
  useAppointmentSessionTypes,
  useChargeItemDefinitions,
  useMeetingTypes,
  useTimeEntryTypes,
  type AppointmentSessionType,
  type CatalogChargeItemDefinition,
  type MeetingType,
  type TimeEntryType,
} from '../calendar/appointmentTypeCatalog';
import { ADMIN_EXT_URLS, EXEMPLAR_NOTE_TAG_CODE, UNIFY_TAG_SYSTEM } from '@unify/fhir-model/constants';
import { UnifyTable, type UnifyTableColumn } from '@/components/UnifyTable';
import { ProcedureCodeSelect } from '@/components/ProcedureCodeSelect';
import type { TemplateListItem } from './template-library/types';
import { useTemplates } from './template-library/hooks';
import { TemplatePreviewDrawer } from './template-library/components/TemplatePreviewDrawer';
import { RulesEnginePage } from './rules-engine';
import { NotificationPreferencesSettings } from './components/NotificationPreferencesSettings';
import { AiDocumentationOptOutCard } from './components/AiDocumentationOptOutCard';
import { upsertProfileExtensions } from '../../lib/profileUserExtensions';
import { ServicesSettings } from './sessions/ServicesSettings';
import { PayersSettings } from './payers/PayersSettings';
import { BillingEntitiesSettings } from './billing-entities/BillingEntitiesSettings';
import { ClinicalSettingsPage } from './clinical/ClinicalSettingsPage';
import { canAccessSystemIntegrationsSettings } from './system-integrations/systemIntegrationsAccess';
import { ZoomLicenseStatusCard } from './system-integrations/ZoomLicenseStatusCard';
import { useCurrentUserPractitioner } from '../../hooks/useCurrentUserPractitioner';
import { FEATURE_VISIBILITY, isClinicalSettingsVisible, isLocalhostDevShell } from '../../config/featureVisibility';
import { LocalhostOnlyBadge } from '../../components/LocalhostOnlyBadge';
import { LocalhostPreviewSettings } from './components/LocalhostPreviewSettings';

type SettingsSection =
  | 'profile'
  | 'notifications'
  | 'calendar'
  | 'appointmentTypes'
  | 'billing'
  | 'security'
  | 'practicesUsers'
  | 'practice'
  | 'users'
  | 'clinical'
  | 'sessions'
  | 'payers'
  | 'billingEntities'
  | 'documentation'
  | 'rulesEngine'
  | 'emailNotifications'
  | 'reports'
  | 'integrations'
  | 'myIntegrations'
  | 'compliance'
  | 'theming'
  | 'stylingPlayground'
  | 'localhostPreview';

type SettingsNavGroupId = 'my-settings' | 'program-settings' | 'it-settings';

const SETTINGS_GROUP_LABEL: Record<SettingsNavGroupId, string> = {
  'my-settings': 'My settings',
  'program-settings': 'Program settings',
  'it-settings': 'IT settings',
};

type SettingsNavItemDef = {
  value: SettingsSection;
  slug: string;
  label: ReactNode;
  icon: ElementType;
  group: SettingsNavGroupId;
  /** When set, sidebar links to this path instead of `/dashboard/settings/:slug`. */
  href?: string;
};

function settingsNavTwoLine(a: string, b: string): ReactElement {
  return (
    <Stack gap={0} style={{ color: 'inherit' }}>
      <Text component="span" size="sm" lh={1.25} fw={500} c="inherit">
        {a}
      </Text>
      <Text component="span" size="sm" lh={1.25} fw={500} c="inherit">
        {b}
      </Text>
    </Stack>
  );
}

/** Declarative nav metadata (My settings: fixed order; other groups: A–Z by label). */
const SETTINGS_NAV: SettingsNavItemDef[] = [
  { value: 'profile', slug: 'profile', label: 'Profile', icon: IconUser, group: 'my-settings' },
  { value: 'notifications', slug: 'notifications', label: 'Notifications', icon: IconBell, group: 'my-settings' },
  { value: 'security', slug: 'security', label: 'Security', icon: IconShield, group: 'my-settings' },
  {
    value: 'myIntegrations',
    slug: 'my-integrations',
    label: 'My Integrations',
    icon: IconLink,
    group: 'my-settings',
  },
  { value: 'calendar', slug: 'calendar', label: 'Calendar', icon: IconCalendar, group: 'my-settings' },
  {
    value: 'billing',
    slug: 'billing',
    label: 'Billing & Finances',
    icon: IconCreditCard,
    group: 'program-settings',
  },
  {
    value: 'appointmentTypes',
    slug: 'appointment-types',
    label: 'Sessions & Services',
    icon: IconCalendarEvent,
    group: 'program-settings',
  },
  { value: 'clinical', slug: 'clinical', label: 'Clinical settings', icon: IconClipboardList, group: 'program-settings' },
  { value: 'sessions', slug: 'sessions', label: 'EOC Types', icon: IconCalendarEvent, group: 'program-settings' },
  { value: 'payers', slug: 'payers', label: 'Payers', icon: IconBuildingBank, group: 'program-settings' },
  { value: 'billingEntities', slug: 'billing-entities', label: 'Billing entities', icon: IconReceipt, group: 'program-settings' },
  { value: 'documentation', slug: 'documentation', label: 'Documentation', icon: IconFileText, group: 'program-settings' },
  {
    value: 'emailNotifications',
    slug: 'email-notifications',
    label: settingsNavTwoLine('Email', 'Notifications'),
    icon: IconMail,
    group: 'program-settings',
  },
  { value: 'practice', slug: 'practice', label: 'Practice settings', icon: IconBriefcase, group: 'program-settings' },
  {
    value: 'practicesUsers',
    slug: 'practices-users',
    label: 'Practices & Users',
    icon: IconUsersGroup,
    group: 'program-settings',
  },
  {
    value: 'reports',
    slug: 'reports',
    label: 'Reports & Analytics',
    icon: IconChartBar,
    group: 'program-settings',
  },
  { value: 'rulesEngine', slug: 'rules-engine', label: 'Rules Engine', icon: IconSettings, group: 'program-settings' },
  { value: 'users', slug: 'users', label: 'Users', icon: IconUsersGroup, group: 'program-settings' },
  {
    value: 'compliance',
    slug: 'compliance',
    label: settingsNavTwoLine('Compliance &', 'Audit Logs'),
    icon: IconLock,
    group: 'it-settings',
  },
  {
    value: 'stylingPlayground',
    slug: 'styling-playground',
    label: 'Styling Playground',
    icon: IconWorld,
    group: 'it-settings',
    href: '/dashboard/styling/overview',
  },
  {
    value: 'integrations',
    slug: 'integrations',
    label: 'System Integrations',
    icon: IconDatabase,
    group: 'it-settings',
  },
  {
    value: 'localhostPreview',
    slug: 'localhost-preview',
    label: 'Localhost preview',
    icon: IconDevices,
    group: 'it-settings',
  },
];

/** Resolve a URL slug to the corresponding SettingsSection value. */
function sectionFromSlug(slug: string | undefined): SettingsSection {
  if (!slug) return 'profile';
  const match = SETTINGS_NAV.find((item) => item.slug === slug);
  return match ? match.value : 'profile';
}

/** Settings sections visible to the Provider role */
const PROVIDER_SETTINGS_SLUGS = new Set([
  'profile',
  'notifications',
  'security',
  'my-integrations',
  'calendar',
]);

/** Settings sections visible to the Client role */
const CLIENT_SETTINGS_SLUGS = new Set(['notifications', 'security']);

function isSettingsNavItemVisible(
  userRole: string,
  item: SettingsNavItemDef,
  canSeeSystemIntegrations: boolean,
  roleSlug?: string,
): boolean {
  if (item.slug === 'billing-entities') return roleSlug === 'unify-admin';
  if (item.slug === 'clinical') return isClinicalSettingsVisible(userRole, roleSlug);
  if (userRole === 'Client' && !CLIENT_SETTINGS_SLUGS.has(item.slug)) return false;
  if (item.slug === 'localhost-preview') return isLocalhostDevShell();
  if (item.slug === 'styling-playground' && userRole !== 'Unify Admin') return false;
  if (item.slug === 'styling-playground' && !FEATURE_VISIBILITY.settingsStylingPlayground) return false;
  if (item.slug === 'rules-engine' && userRole !== 'Unify Admin') return false;
  if (item.slug === 'email-notifications' && userRole !== 'Unify Admin') return false;
  if (item.slug === 'compliance' && userRole !== 'Unify Admin') return false;
  if (item.slug === 'appointment-types' && userRole !== 'Unify Admin') return false;
  if (item.slug === 'notifications' && !FEATURE_VISIBILITY.settingsNotifications) return false;
  if (item.slug === 'sessions' && !FEATURE_VISIBILITY.settingsEocTypes && userRole !== 'Unify Admin') return false;
  if (item.slug === 'payers' && !FEATURE_VISIBILITY.settingsPayers && userRole !== 'Unify Admin') return false;
  if (item.slug === 'documentation' && !FEATURE_VISIBILITY.settingsDocumentation && userRole !== 'Unify Admin') return false;
  if (item.slug === 'reports' && !FEATURE_VISIBILITY.settingsReports) return false;
  if (item.slug === 'integrations' && !canSeeSystemIntegrations) return false;
  if (userRole === 'Provider' && !PROVIDER_SETTINGS_SLUGS.has(item.slug)) return false;
  if (item.slug === 'practice' && (userRole === 'Provider' || userRole === 'Unify Admin')) return false;
  if (item.slug === 'users' && (userRole === 'Provider' || userRole === 'Unify Admin')) return false;
  if (item.slug === 'practices-users' && (userRole === 'Practice Owner' || userRole === 'Practice Admin')) return false;
  if (item.slug === 'billing' && !FEATURE_VISIBILITY.settingsBilling) return false;
  if (item.slug === 'billing' && (userRole === 'Practice Owner' || userRole === 'Practice Admin')) return false;
  return true;
}

function settingsNavHref(item: SettingsNavItemDef): string {
  return item.href ?? `/dashboard/settings/${item.slug}`;
}

function isSettingsNavItemActive(
  item: SettingsNavItemDef,
  pathname: string,
  activeSection: SettingsSection,
): boolean {
  if (item.value === 'stylingPlayground') {
    return (
      pathname === '/dashboard/styling/overview' ||
      pathname === '/dashboard/styling' ||
      pathname === '/dashboard/styling/'
    );
  }
  if (item.href?.startsWith('/dashboard/styling')) {
    return false;
  }
  return activeSection === item.value;
}

function SettingsSidebarNavItem({
  item,
  active,
  onNavigate,
}: {
  item: SettingsNavItemDef;
  active: boolean;
  onNavigate: () => void;
}): JSX.Element {
  const Icon = item.icon;
  return (
    <UnifyNavLink
      label={item.label}
      icon={<Icon size={20} stroke={1.5} />}
      active={active}
      onNavigate={onNavigate}
    />
  );
}

export function SettingsPage(): JSX.Element {
  const { section: sectionSlug } = useParams<{ section?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width: 48em)', false, { getInitialValueInEffect: false });
  const activeSection = sectionFromSlug(sectionSlug);
  const userRole = useEffectiveDisplayUserRole();
  const rbac = usePermissions();
  const canSeeAuditLogs = canAccessAuditLogsUi(rbac);
  const canSeeSystemIntegrations =
    rbac.ready &&
    canAccessSystemIntegrationsSettings(rbac.availableRoles, userRole);
  const showingMobileInbox = isMobile && !sectionSlug;
  const showSidebar = !isMobile || showingMobileInbox;
  const showContent = !isMobile || Boolean(sectionSlug);

  const visibleNavGrouped = useMemo(() => {
    const visible = SETTINGS_NAV.filter((item) => {
      if (!isSettingsNavItemVisible(userRole, item, canSeeSystemIntegrations, rbac.role)) return false;
      if (item.slug === 'compliance' && !canSeeAuditLogs) return false;
      return true;
    });
    const groupOrder: SettingsNavGroupId[] = ['my-settings', 'program-settings', 'it-settings'];
    return groupOrder
      .map((group) => ({
        group,
        title: SETTINGS_GROUP_LABEL[group],
        items: visible.filter((i) => i.group === group),
      }))
      .filter((g) => g.items.length > 0);
  }, [userRole, canSeeAuditLogs, canSeeSystemIntegrations, rbac.role]);

  useEffect(() => {
    if (!rbac.ready) return;
    if (!sectionSlug) {
      if (!isMobile) {
        const fallback =
          SETTINGS_NAV.find(
            (item) =>
              isSettingsNavItemVisible(userRole, item, canSeeSystemIntegrations, rbac.role) &&
              (item.slug !== 'compliance' || canSeeAuditLogs),
          )?.slug ?? 'profile';
        navigate(`/dashboard/settings/${fallback}`, { replace: true });
      }
      return;
    }
    const match = SETTINGS_NAV.find((i) => i.slug === sectionSlug);
    const allowed =
      match &&
      isSettingsNavItemVisible(userRole, match, canSeeSystemIntegrations, rbac.role) &&
      (match.slug !== 'compliance' || canSeeAuditLogs);
    if (allowed) {
      return;
    }
    if (isMobile) {
      navigate('/dashboard/settings', { replace: true });
      return;
    }
    const fallback =
      SETTINGS_NAV.find(
        (item) =>
          isSettingsNavItemVisible(userRole, item, canSeeSystemIntegrations, rbac.role) &&
          (item.slug !== 'compliance' || canSeeAuditLogs),
      )?.slug ?? 'profile';
    navigate(`/dashboard/settings/${fallback}`, { replace: true });
  }, [userRole, sectionSlug, navigate, isMobile, canSeeAuditLogs, canSeeSystemIntegrations, rbac.ready, rbac.role]);

  const handleBackToSettingsInbox = useCallback(() => {
    navigate('/dashboard/settings');
  }, [navigate]);

  const activeSectionLabel = useMemo(() => {
    const match = SETTINGS_NAV.find((item) => item.value === activeSection);
    if (!match) return 'Settings';
    return typeof match.label === 'string' ? match.label : 'Settings';
  }, [activeSection]);

  const renderContent = () => {
    switch (activeSection) {
      case 'profile':
        return <ProfileSettings />;
      case 'notifications':
        return <NotificationsSettings />;
      case 'calendar':
        return <CalendarSettings view="calendar" />;
      case 'appointmentTypes':
        return <CalendarSettings view="appointment-types" />;
      case 'billing':
        return <BillingSettings />;
      case 'security':
        return <SecuritySettings />;
      case 'practicesUsers':
        return <PracticesUsersSettings />;
      case 'practice':
        return <PracticeSettingsWrapper />;
      case 'users':
        return <UsersSectionWrapper />;
      case 'clinical':
        return <ClinicalSettingsPage />;
      case 'sessions':
        return <ServicesSettings />;
      case 'payers':
        return <PayersSettings />;
      case 'billingEntities':
        return <BillingEntitiesSettings />;
      case 'documentation':
        return <DocumentationSettings />;
      case 'rulesEngine':
        return <RulesEnginePage />;
      case 'emailNotifications':
        return <EmailNotificationsSettings />;
      case 'reports':
        return <ReportsSettings />;
      case 'integrations':
        if (!rbac.ready) {
          return (
            <Container size="lg" py="xl">
              <Group justify="center">
                <Loader size="sm" />
                <Text size="sm" c="dimmed">
                  Loading permissions…
                </Text>
              </Group>
            </Container>
          );
        }
        return canSeeSystemIntegrations ? (
          <IntegrationsSettings />
        ) : (
          <Navigate to="/dashboard" replace />
        );
      case 'myIntegrations':
        return <IntegrationsSettings personal />;
      case 'compliance':
        return canSeeAuditLogs ? <AuditLogsPage /> : <Navigate to="/dashboard" replace />;
      case 'theming':
        return userRole === 'Unify Admin' ? (
          <Navigate to="/dashboard/styling/foundations/colors" replace />
        ) : (
          <ProfileSettings />
        );
      case 'stylingPlayground':
        return userRole === 'Unify Admin' ? (
          <Navigate to="/dashboard/styling/overview" replace />
        ) : (
          <ProfileSettings />
        );
      case 'localhostPreview':
        return isLocalhostDevShell() ? <LocalhostPreviewSettings /> : <Navigate to="/dashboard/settings" replace />;
      default:
        return <ProfileSettings />;
    }
  };

  return (
    <Box
      style={{
        maxWidth: '100%',
        width: '100%',
      }}
    >
      <Box
        style={{
          display: 'flex',
          height: 'calc(100vh - 60px)',
          background: 'var(--unify-bg-warm)',
        }}
      >
        {showSidebar ? (
          <Box
            style={{
              width: showingMobileInbox ? '100%' : 260,
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              background: 'var(--unify-bg-sidebar)',
              borderRight: showingMobileInbox ? undefined : '1px solid #9CA3AF',
            }}
          >
            <Box
              style={{
                height: 60,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                paddingLeft: 16,
                paddingRight: 16,
              }}
            >
              <Text size="lg" fw={600} c="gray.9">
                Settings
              </Text>
            </Box>
            <Box style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
              <Stack gap={0} px="sm" pb="md">
                {visibleNavGrouped.map((section, sectionIndex) => (
                  <Box key={section.group} mt={sectionIndex > 0 ? 'md' : 0}>
                    <Text
                      size="xs"
                      tt="uppercase"
                      fw={600}
                      c="dimmed"
                      px={10}
                      mb={6}
                      style={{ letterSpacing: '0.04em' }}
                    >
                      {section.title}
                    </Text>
                    <Stack gap={2}>
                      {section.items.map((item) => (
                        <SettingsSidebarNavItem
                          key={item.value}
                          item={item}
                          active={isSettingsNavItemActive(item, location.pathname, activeSection)}
                          onNavigate={() => navigate(settingsNavHref(item))}
                        />
                      ))}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Box>
        ) : null}
        {showContent ? (
          <Box style={{ flex: 1, minWidth: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
            {isMobile && sectionSlug ? (
              <Box
                px="md"
                py="sm"
                style={{
                  flexShrink: 0,
                  borderBottom: '1px solid #9CA3AF',
                  background: 'var(--unify-bg-warm)',
                }}
              >
                <Group gap="sm" wrap="nowrap">
                  <ActionIcon
                    variant="subtle"
                    size="lg"
                    onClick={handleBackToSettingsInbox}
                    aria-label="Back to settings"
                  >
                    <IconArrowLeft size={20} />
                  </ActionIcon>
                  <Text size="lg" fw={600} c="gray.9" style={{ minWidth: 0 }}>
                    {activeSectionLabel}
                  </Text>
                </Group>
              </Box>
            ) : null}
            <Box style={{ flex: 1, minHeight: 0 }}>{sectionSlug ? renderContent() : null}</Box>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

/** Build a minimal Client from a User so the client Personal Information section can render. */
function clientFromUser(user: User): Client {
  const [firstName = '', lastName = ''] = user.name.split(/\s+/).filter(Boolean);
  return {
    id: user.id,
    name: user.name,
    firstName: firstName || user.name,
    lastName: lastName || '',
    preferredFirstName: firstName || '',
    preferredLastName: lastName || '',
    pronouns: '',
    clientType: 'Adult',
    status: user.status === 'Active' ? 'Active' : user.status === 'Inactive' ? 'Inactive' : 'Unresponsive',
    relationshipType: 'Primary',
    firstSession: user.createdAt || '',
    mostRecentAppointment: user.lastActive || '',
    nextAppointment: '',
    outstandingItems: [],
    provider: '',
    phone: user.phone,
    email: user.email,
    practice: user.practiceName,
  };
}

function splitFullName(fullName: string): HumanName {
  const parts = fullName.trim().split(/\s+/);
  const family = parts.length > 1 ? parts.pop() ?? '' : '';
  const given = parts.length ? parts : [fullName.trim()];
  return { use: 'official', family, given };
}

function upsertOfficialName<T extends Practitioner | Patient>(resource: T, fullName: string): T {
  const nextName = splitFullName(fullName);
  return {
    ...resource,
    name: resource.name?.length
      ? resource.name.map((name, index) => (index === 0 ? { ...name, ...nextName } : name))
      : [nextName],
  };
}

function upsertTelecom<T extends Practitioner | Patient>(
  resource: T,
  updates: { email?: string; phone?: string },
  phoneUse: 'work' | 'home',
): T {
  const telecoms: ContactPoint[] = (resource.telecom ?? []).filter(
    (telecom) =>
      !(updates.email !== undefined && telecom.system === 'email') &&
      !(updates.phone !== undefined && telecom.system === 'phone'),
  );

  if (updates.email) {
    telecoms.push({ system: 'email', value: updates.email, use: phoneUse });
  }
  if (updates.phone) {
    telecoms.push({ system: 'phone', value: updates.phone, use: phoneUse });
  }

  return { ...resource, telecom: telecoms };
}

function getPractitionerOrganizationReference(practitioner: Practitioner): string | undefined {
  const reference = practitioner.extension?.find(
    (extension) => extension.url === ADMIN_EXT_URLS.organization,
  )?.valueReference?.reference;
  return reference?.startsWith('Organization/') ? reference : undefined;
}

async function updateOwnPractitionerRoleLocations(
  medplum: ReturnType<typeof useMedplum>,
  practitioner: Practitioner,
  locationIds: string[],
): Promise<void> {
  const practitionerId = practitioner.id;
  const organizationReference = getPractitionerOrganizationReference(practitioner);
  if (!practitionerId || !organizationReference) {
    return;
  }

  const [locations, roles] = await Promise.all([
    medplum.searchResources('Location', {
      organization: organizationReference,
      _count: '500',
    }).catch(() => []),
    medplum.searchResources('PractitionerRole', {
      practitioner: `Practitioner/${practitionerId}`,
      _count: '20',
    }).catch(() => []),
  ]);

  const allowedIds = new Set(locations.map((location) => location.id).filter(Boolean));
  const sanitizedIds = locationIds.filter((id) => allowedIds.has(id));
  const locationReferences = sanitizedIds.map((id) => ({ reference: `Location/${id}` }));
  const practitionerRole = (roles as PractitionerRole[]).find(
    (role) => role.organization?.reference === organizationReference,
  );

  if (practitionerRole) {
    await medplum.updateResource({
      ...practitionerRole,
      location: locationReferences,
    });
    return;
  }

  if (locationReferences.length > 0) {
    await medplum.createResource({
      resourceType: 'PractitionerRole',
      active: true,
      practitioner: { reference: `Practitioner/${practitionerId}` },
      organization: { reference: organizationReference },
      location: locationReferences,
    });
  }
}

function PracticeSettingsWrapper(): JSX.Element {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const [searchParams] = useSearchParams();
  const {
    users,
    setUsers,
    practices,
    setPractices,
    clientIdsByOrganizationId,
    loading,
    error,
    createUser,
    reload,
    registerClientForOrganization,
  } = useMedplumDirectoryUsers();

  const practiceMapForMerge = useMemo(() => practicesToUserMap(practices), [practices]);

  const handleUserCreated = useCallback(
    async (payload: CreateUserPayload): Promise<User> => createUser(payload),
    [createUser],
  );

  const handleUpdatePracticeUser = useCallback(
    async (userId: string, updates: Partial<User>) => {
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, ...updates } : u)));
      if (isRoleMembershipOnlyUpdate(updates)) {
        return;
      }
      try {
        const practitioner = await editMember(medplum, userId, updates);
        const fromServer = practitionerToUser(practitioner, practiceMapForMerge);
        if (fromServer) {
          setUsers((prev) =>
            prev.map((u) => {
              if (u.id !== userId) return u;
              return {
                ...u,
                ...fromServer,
                permissions: u.permissions,
                credentialing: u.credentialing,
                locationIds: u.locationIds,
              };
            }),
          );
        } else {
          reload();
        }
      } catch (err) {
        console.error('[admin-actions] edit-members failed:', err);
        reload();
      }
    },
    [medplum, practiceMapForMerge, reload, setUsers],
  );

  const currentUser = useMemo(
    () => resolveDirectoryUserFromProfile(profile, users),
    [profile, users],
  );

  const currentPractice = useMemo(
    () => (currentUser ? practices.find((p) => p.id === currentUser.practiceId) ?? null : null),
    [currentUser, practices],
  );

  const handleUpdatePractice = useCallback(
    async (id: string, updates: Partial<Practice>): Promise<boolean> => {
      setPractices((prev) => prev.map((p) => (p.id === id ? { ...p, ...updates } : p)));

      const le = updates.legalEntityAddress;
      const addrLine = [le?.addressLine1, le?.addressLine2].filter(Boolean) as string[];

      const settingsFields: Parameters<typeof editOrgSettings>[2] = {
        ...(updates.name !== undefined ? { name: updates.name } : {}),
        ...(updates.legalName !== undefined ? { legalName: updates.legalName } : {}),
        ...(updates.taxId !== undefined ? { ein: updates.taxId } : {}),
        ...(updates.npi !== undefined ? { npi: updates.npi } : {}),
        ...((updates.email !== undefined || updates.phone !== undefined)
          ? { contact: { email: updates.email, phone: updates.phone } }
          : {}),
        ...(updates.websiteUrl !== undefined ? { websiteUrl: updates.websiteUrl } : {}),
        ...(updates.cancellationWindowHours !== undefined
          ? { cancellationWindowHours: updates.cancellationWindowHours }
          : {}),
        ...(updates.emailResponseTimeframe !== undefined
          ? { emailResponseTimeframe: updates.emailResponseTimeframe }
          : {}),
        ...(le !== undefined
          ? {
            address: {
              ...(addrLine.length > 0 ? { line: addrLine } : {}),
              ...(le.city ? { city: le.city } : {}),
              ...(le.state ? { state: le.state } : {}),
              ...(le.zipCode ? { postalCode: le.zipCode } : {}),
            },
          }
          : {}),
      };

      const hasSettingsUpdates = Object.keys(settingsFields).length > 0;
      if (hasSettingsUpdates) {
        editOrgSettings(medplum, id, settingsFields).catch((err: unknown) => {
          console.error('[admin-actions] edit-settings failed:', err);
        });
      }

      if (updates.branding) {
        editOrgBrand(medplum, id, {
          primaryColor: updates.branding.primaryColor,
          logoUrl: updates.branding.logoUrl,
        }).catch((err: unknown) => {
          console.error('[admin-actions] edit-brand failed:', err);
        });
      }

      return true;
    },
    [medplum, setPractices, reload],
  );

  if (loading) {
    return (
      <Container size="lg" py="xl">
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="lg" py="xl">
        <Alert color="red" title="Could not load practice settings">
          {error.message}
        </Alert>
      </Container>
    );
  }

  if (!currentUser || !currentPractice) {
    return (
      <Container size="lg" py="xl">
        <Alert color="yellow" title="No practice for this account">
          Your Medplum profile could not be matched to a directory practice (Organization id). Ensure
          this project was seeded and your Practitioner has the admin organization extension and pilot
          tag so Settings can resolve the FHIR Organization id.
        </Alert>
      </Container>
    );
  }

  return (
    <PracticeSettings
      practice={currentPractice}
      directoryUsers={users}
      onBack={() => { }}
      onUpdatePractice={handleUpdatePractice}
      onUpdateUser={handleUpdatePracticeUser}
      onUserCreated={handleUserCreated}
      onClientAdded={(user, context) => {
        if (context?.organizationId) {
          registerClientForOrganization(user.id, context.organizationId);
        }
        setUsers((prev) => (prev.some((u) => u.id === user.id) ? prev : [...prev, user]));
      }}
      onAddClientModalClosed={reload}
      practiceUsers={filterUsersForPractice(users, currentPractice.id, clientIdsByOrganizationId)}
      usePracticeNameAsTitle
      hideBackButton
      initialTab={searchParams.get('tab') || undefined}
    />
  );
}

function UsersSectionWrapper(): JSX.Element {
  const profile = useMedplumProfile();
  const { users, practices, loading, error } = useMedplumDirectoryUsers();

  const currentUser = useMemo(
    () => resolveDirectoryUserFromProfile(profile, users),
    [profile, users],
  );

  const currentPractice = useMemo(
    () => (currentUser ? practices.find((p) => p.id === currentUser.practiceId) ?? null : null),
    [currentUser, practices],
  );

  if (loading && users.length === 0 && practices.length === 0) {
    return (
      <Container size="lg" py="xl">
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="lg" py="xl">
        <Alert color="red" title="Could not load users">
          {error.message}
        </Alert>
      </Container>
    );
  }

  if (!currentUser || !currentPractice) {
    return (
      <Container size="lg" py="xl">
        <Alert color="yellow" title="No directory data">
          No pilot-tagged Practitioners/Patients found. Run the Medplum local seed against this project.
        </Alert>
      </Container>
    );
  }

  return <UsersSectionPage scopePractice={currentPractice} hidePracticesTab />;
}

function ProfileSettings({ mode = 'full' }: { mode?: 'full' | 'permissions' } = {}): JSX.Element {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const emptyPracticeMap = useMemo(() => new Map<string, { id: string; name: string }>(), []);

  // Self-service profile is built from the session Practitioner/Patient identity only.
  // The shared admin directory fetch lists all Organizations and pilot-tagged users, which
  // many AccessPolicies forbid. Do not hydrate the form from `useMedplumProfile()`: that
  // cache is not updated by `updateResource`, so navigating away and back would restore
  // pre-save values.
  const [displayUser, setDisplayUser] = useState<User | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(true);

  const profileId = profile?.id;
  const profileType = profile?.resourceType;
  useEffect(() => {
    if (!profileId || (profileType !== 'Practitioner' && profileType !== 'Patient')) {
      setDisplayUser(null);
      setLoadingProfile(false);
      return;
    }
    let cancelled = false;
    setLoadingProfile(true);
    void loadFreshDirectoryUserFromProfile(medplum, profileType, profileId)
      .then((user) => {
        if (cancelled) return;
        setDisplayUser(user);
        setLoadingProfile(false);
      })
      .catch(() => {
        if (cancelled) return;
        setDisplayUser(null);
        setLoadingProfile(false);
      });
    return () => {
      cancelled = true;
    };
  }, [medplum, profileId, profileType]);

  const handleUpdateUser = useCallback(
    async (
      userId: string,
      updates: Partial<User>,
      options: {
        silentSuccess?: boolean;
        silentError?: boolean;
        throwOnError?: boolean;
        shouldApplyResult?: () => boolean;
      } = {},
    ) => {
      if (!options.shouldApplyResult) {
        setDisplayUser((prev) => (prev && prev.id === userId ? { ...prev, ...updates } : prev));
      }

      if (isRoleMembershipOnlyUpdate(updates)) {
        return;
      }

      try {
        if (profile?.resourceType === 'Practitioner') {
          let practitioner = await medplum.readResource('Practitioner', userId, PROFILE_RESOURCE_READ_OPTIONS);
          if (updates.name !== undefined) {
            practitioner = upsertPractitionerOfficialName(
              practitioner,
              splitDisplayNameToOfficialParts(updates.name),
            );
          }
          if (updates.preferredNames !== undefined) {
            practitioner = upsertPractitionerNickname(practitioner, updates.preferredNames);
            practitioner = stripPractitionerLegacyPreferredNames(practitioner);
          }
          if (updates.email !== undefined || updates.phone !== undefined) {
            practitioner = upsertTelecom(practitioner, {
              email: updates.email,
              phone: updates.phone,
            }, 'work');
          }
          const clinicalForExt = updates.clinical
            ? (() => {
                const { preferredFirstName: _a, preferredLastName: _b, ...rest } = updates.clinical;
                return Object.keys(rest).length ? rest : undefined;
              })()
            : updates.clinical;
          practitioner = upsertProfileExtensions(practitioner, { ...updates, clinical: clinicalForExt });
          const savedPractitioner = await medplum.updateResource(practitioner);

          if (updates.locationIds !== undefined) {
            await updateOwnPractitionerRoleLocations(medplum, savedPractitioner, updates.locationIds);
          }

          medplum.invalidateSearches('Practitioner');
          medplum.invalidateSearches('PractitionerRole');

          const fromServer = practitionerToUser(savedPractitioner, emptyPracticeMap);
          if (options.shouldApplyResult?.() === false) {
            // A newer local draft exists; its queued autosave will apply the final result.
          } else if (fromServer) {
            setDisplayUser((prev) => {
              if (!prev || prev.id !== userId) return prev;
              return {
                ...prev,
                ...fromServer,
                practiceId: fromServer.practiceId || prev.practiceId,
                practiceName: fromServer.practiceName || prev.practiceName,
                permissions: prev.permissions,
                credentialing: prev.credentialing,
                locationIds: updates.locationIds ?? prev.locationIds,
              };
            });
          } else {
            setDisplayUser(resolveDirectoryUserFromProfile(savedPractitioner, []));
          }
        } else if (profile?.resourceType === 'Patient') {
          let patient = await medplum.readResource('Patient', userId, PROFILE_RESOURCE_READ_OPTIONS);
          if (updates.name !== undefined) {
            patient = upsertOfficialName(patient, updates.name);
          }
          if (updates.email !== undefined || updates.phone !== undefined) {
            patient = upsertTelecom(patient, {
              email: updates.email,
              phone: updates.phone,
            }, 'home');
          }
          patient = upsertProfileExtensions(patient, updates);
          const savedPatient = await medplum.updateResource(patient);
          medplum.invalidateSearches('Patient');

          const fromServer = patientToUser(savedPatient);
          if (options.shouldApplyResult?.() === false) {
            // A newer local draft exists; its queued autosave will apply the final result.
          } else if (fromServer) {
            setDisplayUser((prev) => (prev && prev.id === userId ? { ...prev, ...fromServer } : prev));
          } else {
            setDisplayUser(resolveDirectoryUserFromProfile(savedPatient, []));
          }
        } else {
          throw new Error('Unsupported profile type for self-service settings');
        }

        if (!options.silentSuccess) {
          notifications.show({
            title: 'Saved',
            message: 'Profile changes were saved.',
            color: 'green',
          });
        }
      } catch (err) {
        console.error('[admin-actions] edit-members failed:', err);
        if (!options.silentError) {
          notifications.show({
            title: 'Save failed',
            message: err instanceof Error
              ? `${err.message} Your changes remain unsaved.`
              : 'Could not save profile. Your changes remain unsaved.',
            color: 'red',
          });
        }
        if (options.throwOnError) throw err;
      }
    },
    [emptyPracticeMap, medplum, profile?.resourceType],
  );

  if (!profile || loadingProfile) {
    return (
      <Container size="lg" py="xl">
        <Center py="xl"><Loader size="lg" /></Center>
      </Container>
    );
  }

  if (!displayUser) {
    return (
      <Container size="lg" py="xl">
        <Alert color="yellow" title="Could not resolve your profile">
          No user profile matches this Medplum session. Ensure your account is linked to a Practitioner or
          Patient resource.
        </Alert>
      </Container>
    );
  }

  // Staff `User.name` is the preferred display label, not a legal first/last pair, so only a
  // client profile gets the derived Client.
  const displayClient = displayUser.role === 'client' ? clientFromUser(displayUser) : undefined;

  return (
    <UserSettings
      user={displayUser}
      onBack={() => { }}
      onUpdateUser={handleUpdateUser}
      autoSave
      isPlatformView={false}
      allUsers={[displayUser]}
      practices={[]}
      client={displayClient}
      mode={mode}
    />
  );
}

function NotificationsSettings(): JSX.Element {
  return <NotificationPreferencesSettings />;
}

function getSessionId(session: ActiveLoginSession): string {
  return session.loginId ?? session.id ?? `${session.userAgent ?? 'session'}-${session.remoteAddress ?? session.remoteHost ?? 'unknown'}`;
}

function formatSessionTitle(session: ActiveLoginSession): string {
  return session.userAgent || 'Unknown device';
}

function formatSessionSubtitle(session: ActiveLoginSession): string {
  const host = session.remoteHost ?? session.remoteAddress ?? 'Unknown location';
  const seen = session.lastSeen ?? session.createdTime;
  return seen ? `${host} · ${new Date(seen).toLocaleString()}` : host;
}

function SecuritySettings(): JSX.Element {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const navigate = useNavigate();
  const userRole = useEffectiveDisplayUserRole();
  const subjectPatientId = usePortalSubjectPatientId();
  const profileEmail = profile?.telecom?.find((t) => t.system === 'email')?.value?.trim().toLowerCase() ?? '';

  const [loginEmail, setLoginEmail] = useState('');
  const [sessions, setSessions] = useState<ActiveLoginSession[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsError, setSessionsError] = useState<string | null>(null);
  const [passwordResetSending, setPasswordResetSending] = useState(false);
  const [logoutAllSending, setLogoutAllSending] = useState(false);
  const [logoutAllConfirmOpened, setLogoutAllConfirmOpened] = useState(false);

  const isClient = userRole === 'Client';
  const showRecordingConsent = Boolean(subjectPatientId) && isClient;
  const [recordingDecision, setRecordingDecision] = useState<SessionRecordingDecision>('none');
  const [recordingLoading, setRecordingLoading] = useState(false);
  const [recordingSaving, setRecordingSaving] = useState(false);
  const [recordingError, setRecordingError] = useState<string | null>(null);
  const [optInModalOpened, setOptInModalOpened] = useState(false);
  const [aiDecision, setAiDecision] = useState<AiToolsConsentDecision>('none');
  const [aiTranscription, setAiTranscription] = useState<AiTranscriptionDecision | undefined>(undefined);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSaving, setAiSaving] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiOptInModalOpened, setAiOptInModalOpened] = useState(false);

  const email = loginEmail || profileEmail;

  const reloadSessionRecordingConsent = useCallback(async () => {
    if (!showRecordingConsent || !subjectPatientId) {
      setRecordingDecision('none');
      return;
    }
    setRecordingLoading(true);
    setRecordingError(null);
    try {
      const decision = await loadSessionRecordingDecision(medplum, subjectPatientId);
      setRecordingDecision(decision);
    } catch (err) {
      setRecordingError(err instanceof Error ? err.message : String(err));
    } finally {
      setRecordingLoading(false);
    }
  }, [showRecordingConsent, subjectPatientId, medplum]);

  useEffect(() => {
    void reloadSessionRecordingConsent();
  }, [reloadSessionRecordingConsent]);

  const showAiConsent = showRecordingConsent;

  const reloadAiToolsConsent = useCallback(async () => {
    if (!showAiConsent || !subjectPatientId) {
      setAiDecision('none');
      setAiTranscription(undefined);
      return;
    }
    setAiLoading(true);
    setAiError(null);
    try {
      await ensureAiToolsConsent(medplum, { targetPatientId: subjectPatientId });
      const state = await loadAiToolsConsentState(medplum, subjectPatientId);
      setAiDecision(state.decision);
      setAiTranscription(state.transcription);
    } catch (err) {
      setAiError(err instanceof Error ? err.message : String(err));
    } finally {
      setAiLoading(false);
    }
  }, [showAiConsent, subjectPatientId, medplum]);

  useEffect(() => {
    void reloadAiToolsConsent();
  }, [reloadAiToolsConsent]);

  useEffect(() => {
    let cancelled = false;

    setSessionsLoading(true);
    setSessionsError(null);

    fetchAuthMeSnapshot(medplum)
      .then(({ loginEmail: nextLoginEmail, sessions: nextSessions }) => {
        if (cancelled) return;
        setLoginEmail(nextLoginEmail ?? '');
        setSessions(nextSessions);
      })
      .catch((err: unknown) => {
        if (!cancelled) setSessionsError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setSessionsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [medplum]);

  const handlePasswordReset = async () => {
    setPasswordResetSending(true);
    try {
      await triggerPasswordResetEmail(medplum, email);
      notifications.show({
        title: 'Password reset email sent',
        message: 'Check your email for instructions to reset your password.',
        color: 'green',
      });
    } catch (err) {
      notifications.show({
        title: 'Could not send password reset email',
        message: err instanceof Error ? err.message : String(err),
        color: 'red',
      });
    } finally {
      setPasswordResetSending(false);
    }
  };

  const handleLogoutAllDevices = async () => {
    setLogoutAllSending(true);
    try {
      await logoutAllDevices(medplum);
      navigate('/signin');
    } catch (err) {
      notifications.show({
        title: 'Could not log out of all devices',
        message: err instanceof Error ? err.message : String(err),
        color: 'red',
      });
    } finally {
      setLogoutAllSending(false);
      setLogoutAllConfirmOpened(false);
    }
  };

  const handleRecordingToggle = async (checked: boolean) => {
    if (!subjectPatientId || recordingSaving) return;
    const action = getSessionRecordingSwitchUiState(recordingDecision, {
      loading: recordingLoading,
      saving: recordingSaving,
    }).actionForToggle(checked);

    if (action === 'revoke') {
      setRecordingSaving(true);
      try {
        const result = await revokeSessionRecordingConsent(medplum, {
          targetPatientId: subjectPatientId,
        });
        await reloadSessionRecordingConsent();
        const zoomWarning = sessionRecordingZoomResyncWarningMessage(result.zoomResyncFailures);
        notifications.show({
          title: 'Session recording turned off',
          message: zoomWarning ?? 'Your consent to record sessions has been revoked.',
          color: zoomWarning ? 'yellow' : 'green',
        });
      } catch (err) {
        notifications.show({
          title: 'Could not revoke session recording consent',
          message: err instanceof Error ? err.message : String(err),
          color: 'red',
        });
      } finally {
        setRecordingSaving(false);
      }
      return;
    }

    if (action === 'open-opt-in') {
      setOptInModalOpened(true);
    }
  };

  const handleOptInSign = async (signature: SignatureInfo) => {
    // SignatureCaptureModal always sets signatureData/signedAt; guard for the optional type.
    if (!subjectPatientId || !signature.signatureData || !signature.signedAt) return;
    setRecordingSaving(true);
    try {
      const result = await optInSessionRecordingConsent(medplum, {
        targetPatientId: subjectPatientId,
        signature: {
          name: signature.name,
          signatureData: signature.signatureData,
          signedAt: signature.signedAt,
        },
      });
      setOptInModalOpened(false);
      await reloadSessionRecordingConsent();
      const zoomWarning = sessionRecordingZoomResyncWarningMessage(result.zoomResyncFailures);
      notifications.show({
        title: 'Session recording turned on',
        message: zoomWarning ?? 'Your consent to record sessions is now on file.',
        color: zoomWarning ? 'yellow' : 'green',
      });
    } catch (err) {
      notifications.show({
        title: 'Could not save session recording consent',
        message: err instanceof Error ? err.message : String(err),
        color: 'red',
      });
    } finally {
      setRecordingSaving(false);
    }
  };

  const handleAiToggle = async (checked: boolean) => {
    if (!subjectPatientId || aiSaving) return;
    const action = getAiToolsSwitchUiState(aiDecision, {
      loading: aiLoading,
      saving: aiSaving,
    }).actionForToggle(checked);

    if (action === 'revoke') {
      setAiSaving(true);
      try {
        await revokeAiToolsConsent(medplum, { targetPatientId: subjectPatientId });
        await reloadAiToolsConsent();
        notifications.show({
          title: 'AI tools turned off',
          message: 'Your consent to AI tools has been revoked.',
          color: 'green',
        });
      } catch (err) {
        notifications.show({
          title: 'Could not revoke AI tools consent',
          message: err instanceof Error ? err.message : String(err),
          color: 'red',
        });
      } finally {
        setAiSaving(false);
      }
      return;
    }

    if (action === 'open-opt-in') {
      setAiOptInModalOpened(true);
    }
  };

  const handleAiOptInSign = async (signature: SignatureInfo) => {
    if (!subjectPatientId || !signature.signatureData || !signature.signedAt) return;
    setAiSaving(true);
    try {
      await optInAiToolsConsent(medplum, {
        targetPatientId: subjectPatientId,
        signature: {
          name: signature.name,
          signatureData: signature.signatureData,
          signedAt: signature.signedAt,
        },
      });
      setAiOptInModalOpened(false);
      await reloadAiToolsConsent();
      notifications.show({
        title: 'AI tools turned on',
        message: 'Your consent to AI tools is now on file.',
        color: 'green',
      });
    } catch (err) {
      notifications.show({
        title: 'Could not save AI tools consent',
        message: err instanceof Error ? err.message : String(err),
        color: 'red',
      });
    } finally {
      setAiSaving(false);
    }
  };

  const recordingUi = getSessionRecordingSwitchUiState(recordingDecision, {
    loading: recordingLoading,
    saving: recordingSaving,
  });
  const recordingDisabled = !showRecordingConsent || !subjectPatientId || recordingUi.disabled;
  const aiUi = getAiToolsSwitchUiState(aiDecision, { loading: aiLoading, saving: aiSaving });
  const aiDisabled = !showAiConsent || !subjectPatientId || aiUi.disabled;
  const aiTranscriptionLine = aiDecision === 'active'
    ? (aiTranscription === 'permit'
      ? 'Session transcription is allowed, so AI notes may use meeting transcripts.'
      : 'Session transcription is not allowed. Other AI documentation can still be used.')
    : aiDecision === 'revoked'
      ? 'Turn this on to restore AI documentation. Your transcription choice stays as it was on the signed form.'
      : null;

  return (
    <Container size="lg" py={{ base: 'md', sm: 'xl' }} px={{ base: 'md', sm: 0 }}>
      <Stack gap="xl">
        <div>
          <Title order={2} mb="xs">
            Login &amp; security
          </Title>
          <Text size="sm" c="dimmed">
            Manage your account security settings
          </Text>
        </div>

        <Paper p={{ base: 'md', sm: 'xl' }} withBorder>
          <Stack gap="lg">
            <Flex
              direction={{ base: 'column', sm: 'row' }}
              justify="space-between"
              align={{ base: 'stretch', sm: 'center' }}
              gap="md"
            >
              <div style={{ minWidth: 0 }}>
                <Text fw={600} mb={4}>
                  Password
                </Text>
                <Text size="sm" c="dimmed">
                  We&apos;ll send reset instructions to {email || 'your account email'}.
                </Text>
              </div>
              <Button
                variant="outline"
                color="gray"
                loading={passwordResetSending}
                disabled={!email}
                onClick={() => void handlePasswordReset()}
                w={{ base: '100%', sm: 'auto' }}
                style={{ flexShrink: 0 }}
              >
                Send reset email
              </Button>
            </Flex>
            <Divider />
            <Flex
              direction={{ base: 'column', sm: 'row' }}
              justify="space-between"
              align={{ base: 'stretch', sm: 'center' }}
              gap="md"
            >
              <div style={{ minWidth: 0 }}>
                <Text fw={600} mb={4}>
                  Two-factor authentication
                </Text>
                <Text size="sm" c="dimmed">
                  Two-factor authentication setup will be available soon.
                </Text>
              </div>
              <Button variant="outline" color="gray" disabled w={{ base: '100%', sm: 'auto' }} style={{ flexShrink: 0 }}>
                Coming soon
              </Button>
            </Flex>
            {showRecordingConsent ? (
              <>
                <Divider />
                <Flex
                  direction={{ base: 'column', sm: 'row' }}
                  justify="space-between"
                  align={{ base: 'stretch', sm: 'center' }}
                  gap="md"
                >
                  <div style={{ minWidth: 0 }}>
                    <Text fw={600} mb={4}>
                      Consent to have sessions recorded
                    </Text>
                    <Text size="sm" c="dimmed">
                      Allow your therapy sessions to be recorded for clinical documentation.
                    </Text>
                    {recordingError ? (
                      <Text size="sm" c="red" mt={4}>
                        {recordingError}
                      </Text>
                    ) : null}
                  </div>
                  <Group gap="sm" style={{ flexShrink: 0, alignSelf: 'center' }}>
                    {recordingLoading ? <Loader size="xs" /> : null}
                    <Switch
                      size="sm"
                      color="gold"
                      checked={recordingUi.checked}
                      disabled={recordingDisabled}
                      onChange={(e) => void handleRecordingToggle(e.currentTarget.checked)}
                      label={recordingUi.pending ? 'Pending' : undefined}
                      aria-label={
                        recordingUi.pending
                          ? 'Pending — complete the session recording consent form first'
                          : 'Consent to have sessions recorded'
                      }
                    />
                  </Group>
                </Flex>
                <Divider />
                <Flex
                  direction={{ base: 'column', sm: 'row' }}
                  justify="space-between"
                  align={{ base: 'stretch', sm: 'center' }}
                  gap="md"
                >
                  <div style={{ minWidth: 0 }}>
                    <Text fw={600} mb={4}>
                      Consent to AI tools
                    </Text>
                    <Text size="sm" c="dimmed">
                      Allow your providers to use AI to help draft and revise your clinical documentation.
                    </Text>
                    {aiTranscriptionLine ? (
                      <Text size="sm" c="dimmed" mt={4}>
                        {aiTranscriptionLine}
                      </Text>
                    ) : null}
                    {aiError ? (
                      <Text size="sm" c="red" mt={4}>
                        {aiError}
                      </Text>
                    ) : null}
                  </div>
                  <Group gap="sm" style={{ flexShrink: 0, alignSelf: 'center' }}>
                    {aiLoading ? <Loader size="xs" /> : null}
                    <Switch
                      size="sm"
                      color="gold"
                      checked={aiUi.checked}
                      disabled={aiDisabled}
                      onChange={(e) => void handleAiToggle(e.currentTarget.checked)}
                      label={aiUi.pending ? 'Pending' : undefined}
                      aria-label={
                        aiUi.pending
                          ? 'Pending — complete the AI tools consent form first'
                          : 'Consent to AI tools'
                      }
                    />
                  </Group>
                </Flex>
              </>
            ) : null}
            <Divider />
            <Flex
              direction={{ base: 'column', sm: 'row' }}
              justify="space-between"
              align={{ base: 'stretch', sm: 'flex-start' }}
              gap="md"
            >
              <div style={{ minWidth: 0 }}>
                <Text fw={600} mb="xs">
                  Active sessions
                </Text>
                <Text size="sm" c="dimmed">
                  Manage devices where you&apos;re logged in
                </Text>
              </div>
              <Button
                variant="outline"
                color="red"
                loading={logoutAllSending}
                disabled={sessionsLoading}
                onClick={() => setLogoutAllConfirmOpened(true)}
                w={{ base: '100%', sm: 'auto' }}
                style={{ flexShrink: 0 }}
              >
                Log out of all devices
              </Button>
            </Flex>
            {sessionsLoading ? (
              <Center py="md">
                <Loader size="sm" />
              </Center>
            ) : sessionsError ? (
              <Alert color="red" title="Could not load active sessions">
                {sessionsError}
              </Alert>
            ) : sessions.length === 0 ? (
              <Text size="sm" c="dimmed">
                No active devices were returned by Medplum. Sessions appear here for non-revoked logins updated in the last hour.
              </Text>
            ) : (
              <Stack gap="sm">
                {sessions.map((session) => (
                  <Flex
                    key={getSessionId(session)}
                    direction={{ base: 'column', sm: 'row' }}
                    justify="space-between"
                    align={{ base: 'stretch', sm: 'center' }}
                    gap="sm"
                    p="md"
                    style={{
                      border: '1px solid var(--mantine-color-gray-5)',
                      borderRadius: 8,
                    }}
                  >
                    <Group wrap="nowrap" style={{ minWidth: 0 }}>
                      <IconDevices size={24} style={{ flexShrink: 0 }} />
                      <div style={{ minWidth: 0 }}>
                        <Text size="sm" fw={500}>
                          {formatSessionTitle(session)}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {formatSessionSubtitle(session)}
                        </Text>
                      </div>
                    </Group>
                    {session.current && (
                      <Badge color="green" variant="light" style={{ alignSelf: 'flex-start' }}>
                        Current
                      </Badge>
                    )}
                  </Flex>
                ))}
              </Stack>
            )}
          </Stack>
        </Paper>

        {!isClient ? <ProfileSettings mode="permissions" /> : null}

        <Modal
          opened={logoutAllConfirmOpened}
          onClose={() => setLogoutAllConfirmOpened(false)}
          title="Log out of all devices?"
          centered
        >
          <Stack gap="md">
            <Text size="sm">
              This will revoke every active session Medplum returns for your account, including this device.
              You&apos;ll need to sign in again to continue.
            </Text>
            <Group justify="flex-end">
              <Button
                variant="outline"
                color="gray"
                disabled={logoutAllSending}
                onClick={() => setLogoutAllConfirmOpened(false)}
              >
                Cancel
              </Button>
              <Button color="red" loading={logoutAllSending} onClick={() => void handleLogoutAllDevices()}>
                Log out everywhere
              </Button>
            </Group>
          </Stack>
        </Modal>

        <SignatureCaptureModal
          opened={optInModalOpened}
          onClose={() => {
            if (!recordingSaving) setOptInModalOpened(false);
          }}
          onSign={(signature) => void handleOptInSign(signature)}
          title="Consent to session recording"
          submitLabel="Sign and turn on"
          allowSaveForFuture={false}
          reviewWarning="By signing, you consent to having your therapy sessions recorded for clinical documentation."
        />
        <SignatureCaptureModal
          opened={aiOptInModalOpened}
          onClose={() => {
            if (!aiSaving) setAiOptInModalOpened(false);
          }}
          onSign={(signature) => void handleAiOptInSign(signature)}
          title="Consent to AI tools"
          submitLabel="Sign and turn on"
          allowSaveForFuture={false}
          reviewWarning="By signing, you consent to AI tools being used to help draft and revise your clinical documentation. Your session transcription choice stays as it was on your signed form."
        />
      </Stack>
    </Container>
  );
}

type SessionTypeFormState = {
  id: string;
  description: string;
  durationMinutes: string;
  rate: number;
  cptCode: string;
  chargeItemDefinitionId: string | null;
  billingTitle: string;
  codeSystem?: string;
  onlineBooking: boolean;
  active: boolean;
  modality: AppointmentSessionType['modality'];
  noteTemplateId: string | null;
  aiNoteGenerationPrompt: string;
  exemplarNoteQrIds: string[];
};
const ALL_HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => {
  const hour12 = i === 0 ? 12 : i > 12 ? i - 12 : i;
  const period = i < 12 ? 'AM' : 'PM';
  const label = `${hour12}:00 ${period}`;
  return { value: String(i), label };
});

const CALENDAR_TIMEZONE_OPTIONS = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
] as const;

function sortByLabel<T>(rows: T[], label: (row: T) => string): T[] {
  return [...rows].sort((a, b) =>
    label(a).localeCompare(label(b), undefined, { sensitivity: 'base' }),
  );
}

const APPOINTMENT_TYPE_STATUS_FILTER = {
  allLabel: 'All statuses',
  columnKey: 'status',
  data: [
    { value: 'ACTIVE', label: 'Active' },
    { value: 'INACTIVE', label: 'Inactive' },
  ],
  defaultValue: ['ACTIVE'],
  clearable: true,
};

function CalendarSettings({ view }: { view: 'calendar' | 'appointment-types' }): JSX.Element {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const displayRole = useEffectiveDisplayUserRole();
  // Display role collapses unify-admin + unify-assistant -> 'Unify Admin' (see displayUserRole.ts).
  const isUnifyStaff = displayRole === 'Unify Admin';
  const rbac = usePermissions();
  const canEditAiPrompt = isClinicalSettingsVisible(displayRole, rbac.role);
  const [timezone, setTimezone] = useState<string | null>(null);
  const [timezoneSaving, setTimezoneSaving] = useState(false);
  const [timezoneLoading, setTimezoneLoading] = useState(true);
  const practitionerRef = useRef<Practitioner | null>(null);
  const [displayStartHour, setDisplayStartHour] = useState('7');
  const [displayEndHour, setDisplayEndHour] = useState('19');

  const practitionerProfileId = profile?.resourceType === 'Practitioner' ? profile.id : undefined;
  const profileLoaded = Boolean(profile);

  // The cached login profile does not reflect `updateResource` extension writes, so
  // re-read the Practitioner to show the saved timezone (see useGeminiAiOptOut).
  // Stay in the loading state until then so the Select never flashes a stale zone.
  useEffect(() => {
    if (!profileLoaded) return;
    if (!practitionerProfileId) {
      setTimezoneLoading(false);
      return;
    }
    let cancelled = false;
    void medplum
      .readResource('Practitioner', practitionerProfileId, { cache: 'no-cache' })
      .then((practitioner) => {
        if (cancelled) return;
        practitionerRef.current = practitioner;
        const stored = getPractitionerDisplayTimeZone(practitioner);
        const optionValues = new Set<string>(CALENDAR_TIMEZONE_OPTIONS.map((o) => o.value));
        let browser = '';
        try {
          browser = Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
        } catch {
          browser = '';
        }
        setTimezone(stored ?? (optionValues.has(browser) ? browser : null));
      })
      .catch(() => {
        // Leave the Select empty ("Use browser timezone") if the read fails.
      })
      .finally(() => {
        if (!cancelled) setTimezoneLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [medplum, practitionerProfileId, profileLoaded]);

  const saveTimezone = async (next: string) => {
    const previous = timezone;
    setTimezone(next);
    const current =
      practitionerRef.current
      ?? (profile?.resourceType === 'Practitioner' ? (profile as Practitioner) : null);
    if (!current?.id) {
      setTimezone(previous);
      return;
    }
    setTimezoneSaving(true);
    try {
      const saved = await medplum.updateResource(setPractitionerDisplayTimeZone(current, next));
      practitionerRef.current = saved;
      setPreferredDisplayTimeZone(next);
      medplum.invalidateSearches('Practitioner');
      notifications.show({
        color: 'green',
        title: 'Setting saved',
        message: 'Calendar timezone updated.',
      });
    } catch {
      setTimezone(previous);
      notifications.show({
        color: 'red',
        title: 'Could not save setting',
        message: 'Please try again.',
      });
    } finally {
      setTimezoneSaving(false);
    }
  };

  const sessionTypes = sortByLabel(useAppointmentSessionTypes(), (t) => t.description);
  const [sessionTypeModalOpened, setSessionTypeModalOpened] = useState(false);
  const [sessionTypeModalMode, setSessionTypeModalMode] = useState<'add' | 'edit'>('add');
  const [sessionTypeForm, setSessionTypeForm] = useState<SessionTypeFormState>({
    id: '',
    description: '',
    durationMinutes: '50',
    rate: 150,
    cptCode: '',
    chargeItemDefinitionId: null,
    billingTitle: '',
    onlineBooking: true,
    active: true,
    modality: 'either',
    noteTemplateId: null,
    aiNoteGenerationPrompt: DEFAULT_AI_NOTE_GENERATION_SYSTEM_PROMPT,
    exemplarNoteQrIds: [],
  });
  const [promptEditorOpen, setPromptEditorOpen] = useState(false);
  const [exemplarQrOptions, setExemplarQrOptions] = useState<{ value: string; label: string }[]>([]);
  const [exemplarQrLoading, setExemplarQrLoading] = useState(false);

  type MeetingTypeFormState = { id: string; description: string; defaultDurationMinutes: string; active: boolean };
  type TimeEntryTypeFormState = { id: string; description: string; defaultDurationMinutes: string; active: boolean };

  // Per-provider access is edited on the same modal as the appointment type.
  const meetingTypes = sortByLabel(useMeetingTypes(), (t) => t.description);
  const timeEntryTypes = sortByLabel(useTimeEntryTypes(), (t) => t.description);
  const chargeItemDefinitions = sortByLabel(useChargeItemDefinitions(), (t) => t.title);

  const [meetingTypeModalOpened, setMeetingTypeModalOpened] = useState(false);
  const [meetingTypeModalMode, setMeetingTypeModalMode] = useState<'add' | 'edit'>('add');
  const [meetingTypeForm, setMeetingTypeForm] = useState<MeetingTypeFormState>({
    id: '',
    description: '',
    defaultDurationMinutes: '60',
    active: true,
  });

  const [timeEntryTypeModalOpened, setTimeEntryTypeModalOpened] = useState(false);
  const [timeEntryTypeModalMode, setTimeEntryTypeModalMode] = useState<'add' | 'edit'>('add');
  const [timeEntryTypeForm, setTimeEntryTypeForm] = useState<TimeEntryTypeFormState>({
    id: '',
    description: '',
    defaultDurationMinutes: '60',
    active: true,
  });

  type ServiceCodeFormState = {
    id: string;
    catalogKey: string;
    title: string;
    code: string;
    codeSystem?: string;
    rate: number;
    active: boolean;
  };
  const [serviceCodeModalOpened, setServiceCodeModalOpened] = useState(false);
  const [serviceCodeModalMode, setServiceCodeModalMode] = useState<'add' | 'edit'>('add');
  const [serviceCodeForm, setServiceCodeForm] = useState<ServiceCodeFormState>({
    id: '',
    catalogKey: '',
    title: '',
    code: '',
    rate: 0,
    active: true,
  });

  const openNewSessionTypeModal = () => {
    setSessionTypeModalMode('add');
    setSessionTypeForm({
      id: '',
      description: '',
      durationMinutes: '50',
      rate: 150,
      cptCode: '',
      chargeItemDefinitionId: null,
      billingTitle: '',
      onlineBooking: true,
      active: true,
      modality: 'either',
      noteTemplateId: null,
      aiNoteGenerationPrompt: DEFAULT_AI_NOTE_GENERATION_SYSTEM_PROMPT,
      exemplarNoteQrIds: [],
    });
    setPromptEditorOpen(false);
    setExemplarQrOptions([]);
    setSessionTypeModalOpened(true);
  };

  const openEditSessionTypeModal = useCallback((sessionType: AppointmentSessionType) => {
    setSessionTypeModalMode('edit');
    setSessionTypeForm({
      id: sessionType.id,
      description: sessionType.description,
      durationMinutes: String(sessionType.durationMinutes),
      rate: sessionType.rate,
      cptCode: sessionType.cptCode,
      chargeItemDefinitionId: sessionType.chargeItemDefinitionId ?? null,
      billingTitle: sessionType.billingTitle ?? '',
      codeSystem: sessionType.codeSystem,
      onlineBooking: sessionType.onlineBooking,
      active: sessionType.active,
      modality: sessionType.modality,
      noteTemplateId: sessionType.noteTemplateId,
      aiNoteGenerationPrompt: sessionType.aiNoteGenerationPrompt ?? DEFAULT_AI_NOTE_GENERATION_SYSTEM_PROMPT,
      exemplarNoteQrIds: sessionType.exemplarNoteQrIds ?? [],
    });
    setPromptEditorOpen(false);
    setExemplarQrOptions([]);
    setSessionTypeModalOpened(true);
  }, []);

  const openNewMeetingTypeModal = () => {
    setMeetingTypeModalMode('add');
    setMeetingTypeForm({
      id: '',
      description: '',
      defaultDurationMinutes: '60',
      active: true,
    });
    setMeetingTypeModalOpened(true);
  };

  const openEditMeetingTypeModal = useCallback((row: MeetingType) => {
    setMeetingTypeModalMode('edit');
    setMeetingTypeForm({
      id: row.id,
      description: row.description,
      defaultDurationMinutes: String(row.defaultDurationMinutes),
      active: row.active,
    });
    setMeetingTypeModalOpened(true);
  }, []);

  const handleSaveMeetingType = async () => {
    const trimmedDescription = meetingTypeForm.description.trim();
    if (!trimmedDescription) return;
    const parsedDuration = Number(meetingTypeForm.defaultDurationMinutes);
    if (!Number.isFinite(parsedDuration) || parsedDuration <= 0) return;
    await upsertMeetingType(medplum, {
      id: meetingTypeModalMode === 'edit' ? meetingTypeForm.id : '',
      description: trimmedDescription,
      defaultDurationMinutes: parsedDuration,
      active: meetingTypeForm.active,
    });
    setMeetingTypeModalOpened(false);
  };

  const openNewTimeEntryTypeModal = () => {
    setTimeEntryTypeModalMode('add');
    setTimeEntryTypeForm({
      id: '',
      description: '',
      defaultDurationMinutes: '60',
      active: true,
    });
    setTimeEntryTypeModalOpened(true);
  };

  const openEditTimeEntryTypeModal = useCallback((row: TimeEntryType) => {
    setTimeEntryTypeModalMode('edit');
    setTimeEntryTypeForm({
      id: row.id,
      description: row.description,
      defaultDurationMinutes: String(row.defaultDurationMinutes),
      active: row.active,
    });
    setTimeEntryTypeModalOpened(true);
  }, []);

  const handleSaveTimeEntryType = async () => {
    const trimmedDescription = timeEntryTypeForm.description.trim();
    if (!trimmedDescription) return;
    const parsedDuration = Number(timeEntryTypeForm.defaultDurationMinutes);
    if (!Number.isFinite(parsedDuration) || parsedDuration <= 0) return;
    await upsertTimeEntryType(medplum, {
      id: timeEntryTypeModalMode === 'edit' ? timeEntryTypeForm.id : '',
      description: trimmedDescription,
      defaultDurationMinutes: parsedDuration,
      active: timeEntryTypeForm.active,
    });
    setTimeEntryTypeModalOpened(false);
  };

  const openNewServiceCodeModal = () => {
    setServiceCodeModalMode('add');
    setServiceCodeForm({
      id: '',
      catalogKey: '',
      title: '',
      code: '',
      codeSystem: undefined,
      rate: 0,
      active: true,
    });
    setServiceCodeModalOpened(true);
  };

  const openEditServiceCodeModal = useCallback((row: CatalogChargeItemDefinition) => {
    setServiceCodeModalMode('edit');
    setServiceCodeForm({
      id: row.id,
      catalogKey: row.catalogKey,
      title: row.title,
      code: row.code,
      codeSystem: row.codeSystem,
      rate: row.rate,
      active: row.active,
    });
    setServiceCodeModalOpened(true);
  }, []);

  const handleSaveServiceCode = async () => {
    const title = serviceCodeForm.title.trim();
    const code = serviceCodeForm.code.trim().toUpperCase();
    if (!title || !code) return;
    await upsertChargeItemDefinitionRow(medplum, {
      id: serviceCodeModalMode === 'edit' ? serviceCodeForm.id : '',
      catalogKey: serviceCodeModalMode === 'edit' ? serviceCodeForm.catalogKey : '',
      title,
      code,
      codeSystem: serviceCodeForm.codeSystem,
      rate: Math.max(0, Number(serviceCodeForm.rate) || 0),
      active: serviceCodeForm.active,
    });
    setServiceCodeModalOpened(false);
  };

  const handleSaveSessionType = async () => {
    const trimmedDescription = sessionTypeForm.description.trim();
    if (!trimmedDescription) return;
    const parsedDuration = Number(sessionTypeForm.durationMinutes);
    if (!Number.isFinite(parsedDuration) || parsedDuration <= 0) return;
    await upsertAppointmentSessionType(medplum, {
      id: sessionTypeModalMode === 'edit' ? sessionTypeForm.id : '',
      description: trimmedDescription,
      durationMinutes: parsedDuration,
      rate: Math.max(0, Number(sessionTypeForm.rate) || 0),
      cptCode: sessionTypeForm.cptCode.trim().toUpperCase(),
      chargeItemDefinitionId: sessionTypeForm.chargeItemDefinitionId ?? undefined,
      billingTitle: sessionTypeForm.billingTitle.trim() || undefined,
      codeSystem: sessionTypeForm.codeSystem,
      onlineBooking: sessionTypeForm.onlineBooking,
      active: sessionTypeForm.active,
      modality: sessionTypeForm.modality,
      noteTemplateId: sessionTypeForm.noteTemplateId,
      aiNoteGenerationPrompt: sessionTypeForm.aiNoteGenerationPrompt.trim() || undefined,
      exemplarNoteQrIds: sessionTypeForm.exemplarNoteQrIds,
    }, { skipAiPrompt: !canEditAiPrompt });
    setSessionTypeModalOpened(false);
    setPromptEditorOpen(false);
  };

  const serviceCodeSelectOptions = useMemo(() => {
    const options = chargeItemDefinitions
      .filter((row) => row.active)
      .map((row) => ({ value: row.id, label: `${row.code} – ${row.title} ($${row.rate})` }));
    // A session type already linked to a retired code keeps its option so saving
    // cannot silently unlink it; only active codes are otherwise selectable.
    const linkedId = sessionTypeForm.chargeItemDefinitionId;
    if (linkedId && !options.some((option) => option.value === linkedId)) {
      const linked = chargeItemDefinitions.find((row) => row.id === linkedId);
      if (linked) {
        options.unshift({
          value: linked.id,
          label: `${linked.code} – ${linked.title} ($${linked.rate}) — inactive`,
        });
      }
    }
    return options;
  }, [chargeItemDefinitions, sessionTypeForm.chargeItemDefinitionId]);

  const { templates: noteTemplates } = useTemplates({ category: 'appointments', statuses: ['active', 'draft'] });
  const noteTemplateOptions = useMemo(
    () => noteTemplates.map((t) => ({ value: t.id, label: t.title })),
    [noteTemplates]
  );
  const [previewTemplate, setPreviewTemplate] = useState<TemplateListItem | null>(null);

  // Load exemplar QR options whenever the selected note template changes.
  useEffect(() => {
    if (!sessionTypeModalOpened || !sessionTypeForm.noteTemplateId) {
      setExemplarQrOptions([]);
      return;
    }
    let cancelled = false;
    setExemplarQrLoading(true);
    medplum
      .searchResources('QuestionnaireResponse', {
        questionnaire: `Questionnaire/${sessionTypeForm.noteTemplateId}`,
        _tag: `${UNIFY_TAG_SYSTEM}|${EXEMPLAR_NOTE_TAG_CODE}`,
        _count: '50',
      })
      .then((qrs) => {
        if (cancelled) return;
        setExemplarQrOptions(
          qrs
            .filter((qr) => qr.id)
            .map((qr) => {
              const titleExt = qr.extension?.find(
                (e) => e.url === 'https://unify.health/fhir/StructureDefinition/exemplar-note-title',
              )?.valueString;
              const identifierValue = qr.identifier?.value;
              const authoredDate = qr.authored ? new Date(qr.authored).toLocaleDateString() : undefined;
              const label =
                titleExt ?? identifierValue ?? authoredDate ?? `QuestionnaireResponse/${qr.id}`;
              return { value: qr.id!, label };
            }),
        );
      })
      .catch(() => {
        if (!cancelled) setExemplarQrOptions([]);
      })
      .finally(() => {
        if (!cancelled) setExemplarQrLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [medplum, sessionTypeModalOpened, sessionTypeForm.noteTemplateId]);

  const sessionTypeColumns: UnifyTableColumn<AppointmentSessionType>[] = useMemo(
    () => [
      {
        key: 'description',
        label: 'Description',
        width: '40%',
        getValue: (row) => row.description,
        render: (row) => (
          <Text size="sm">{row.description}</Text>
        ),
      },
      {
        key: 'duration',
        label: 'Default Duration',
        width: 140,
        getValue: (row) => String(row.durationMinutes),
        render: (row) => <Text size="sm">{row.durationMinutes} min</Text>,
      },
      {
        key: 'rate',
        label: 'Default Rate',
        width: 120,
        getValue: (row) => String(row.rate),
        render: (row) => <Text size="sm">{row.cptCode ? `$${row.rate}` : '—'}</Text>,
      },
      {
        key: 'cptCode',
        label: 'Default CPT Code',
        width: 140,
        getValue: (row) => row.cptCode,
        render: (row) => <Text size="sm">{row.cptCode || '—'}</Text>,
      },
      {
        key: 'status',
        label: 'Status',
        width: 120,
        align: 'right',
        getValue: (row) => (row.active ? 'ACTIVE' : 'INACTIVE'),
        render: (row) => (
          <Badge color={row.active ? 'green' : 'gray'} variant="light" size="sm">
            {row.active ? 'ACTIVE' : 'INACTIVE'}
          </Badge>
        ),
      },
    ],
    []
  );
  const meetingTypeColumns: UnifyTableColumn<MeetingType>[] = useMemo(
    () => [
      {
        key: 'description',
        label: 'Description',
        width: '45%',
        getValue: (row) => row.description,
        render: (row) => <Text size="sm">{row.description}</Text>,
      },
      {
        key: 'defaultDuration',
        label: 'Default duration',
        width: 160,
        getValue: (row) => String(row.defaultDurationMinutes),
        render: (row) => <Text size="sm">{row.defaultDurationMinutes} min</Text>,
      },
      {
        key: 'status',
        label: 'Status',
        width: 120,
        align: 'right',
        getValue: (row) => (row.active ? 'ACTIVE' : 'INACTIVE'),
        render: (row) => (
          <Badge color={row.active ? 'green' : 'gray'} variant="light" size="sm">
            {row.active ? 'ACTIVE' : 'INACTIVE'}
          </Badge>
        ),
      },
    ],
    []
  );
  const timeEntryTypeColumns: UnifyTableColumn<TimeEntryType>[] = useMemo(
    () => [
      {
        key: 'description',
        label: 'Description',
        width: '45%',
        getValue: (row) => row.description,
        render: (row) => <Text size="sm">{row.description}</Text>,
      },
      {
        key: 'defaultDuration',
        label: 'Default duration',
        width: 160,
        getValue: (row) => String(row.defaultDurationMinutes),
        render: (row) => <Text size="sm">{row.defaultDurationMinutes} min</Text>,
      },
      {
        key: 'status',
        label: 'Status',
        width: 120,
        align: 'right',
        getValue: (row) => (row.active ? 'ACTIVE' : 'INACTIVE'),
        render: (row) => (
          <Badge color={row.active ? 'green' : 'gray'} variant="light" size="sm">
            {row.active ? 'ACTIVE' : 'INACTIVE'}
          </Badge>
        ),
      },
    ],
    []
  );
  const serviceCodeColumns: UnifyTableColumn<CatalogChargeItemDefinition>[] = useMemo(
    () => [
      {
        key: 'title',
        label: 'Title',
        getValue: (row) => row.title,
        render: (row) => <Text size="sm">{row.title}</Text>,
      },
      {
        key: 'rate',
        label: 'Default rate',
        getValue: (row) => String(row.rate),
        render: (row) => <Text size="sm">${row.rate}</Text>,
      },
      {
        key: 'code',
        label: 'CPT code',
        getValue: (row) => row.code,
        render: (row) => <Text size="sm">{row.code}</Text>,
      },
      {
        key: 'status',
        label: 'Status',
        getValue: (row) => (row.active ? 'ACTIVE' : 'INACTIVE'),
        render: (row) => (
          <Badge color={row.active ? 'green' : 'gray'} variant="light" size="sm">
            {row.active ? 'ACTIVE' : 'INACTIVE'}
          </Badge>
        ),
      },
    ],
    [],
  );

  const isAppointmentTypesView = view === 'appointment-types';

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        <Group justify="space-between" align="flex-start">
          <div>
            <Title order={2} mb="xs">
              {isAppointmentTypesView ? 'Sessions & Services' : 'Calendar'}
            </Title>
            <Text size="sm" c="dimmed">
              {isAppointmentTypesView
                ? 'Configure session, meeting, and time entry types'
                : 'Configure your calendar timezone and display hours'}
            </Text>
          </div>
        </Group>

        {isAppointmentTypesView ? (
          <Stack gap="xl">
              <UnifyTable
                title="Session types"
                subtitle="Individual and couples therapy sessions"
                headerRight={
                  <Button leftSection={<IconPlus size={16} />} color="gold" onClick={openNewSessionTypeModal}>
                    Add session type
                  </Button>
                }
                columns={sessionTypeColumns}
                data={sessionTypes}
                getRowKey={(row) => row.id}
                sortable
                searchable
                searchPlaceholder="Search session types"
                onRowClick={openEditSessionTypeModal}
                multiFilter={APPOINTMENT_TYPE_STATUS_FILTER}
                tableProps={{ highlightOnHover: true, style: { tableLayout: 'fixed' } }}
              />

              <UnifyTable
                title="Meeting types"
                subtitle="Internal meetings and supervision"
                headerRight={
                  <Button leftSection={<IconPlus size={16} />} color="gold" onClick={openNewMeetingTypeModal}>
                    Add meeting type
                  </Button>
                }
                columns={meetingTypeColumns}
                data={meetingTypes}
                getRowKey={(row) => row.id}
                sortable
                searchable
                searchPlaceholder="Search meeting types"
                onRowClick={openEditMeetingTypeModal}
                multiFilter={APPOINTMENT_TYPE_STATUS_FILTER}
                tableProps={{ highlightOnHover: true, style: { tableLayout: 'fixed' } }}
              />

              <UnifyTable
                title="Time entry types"
                subtitle="Documentation, admin work, and breaks"
                headerRight={
                  <Button leftSection={<IconPlus size={16} />} color="gold" onClick={openNewTimeEntryTypeModal}>
                    Add time entry type
                  </Button>
                }
                columns={timeEntryTypeColumns}
                data={timeEntryTypes}
                getRowKey={(row) => row.id}
                sortable
                searchable
                searchPlaceholder="Search time entry types"
                onRowClick={openEditTimeEntryTypeModal}
                multiFilter={APPOINTMENT_TYPE_STATUS_FILTER}
                tableProps={{ highlightOnHover: true, style: { tableLayout: 'fixed' } }}
              />

              <UnifyTable
                title="Service Codes"
                headerRight={
                  isUnifyStaff ? (
                    <Button leftSection={<IconPlus size={16} />} color="gold" onClick={openNewServiceCodeModal}>
                      Add service code
                    </Button>
                  ) : undefined
                }
                columns={serviceCodeColumns}
                data={chargeItemDefinitions}
                getRowKey={(row) => row.id}
                sortable
                searchable
                searchPlaceholder="Search service codes"
                multiFilter={APPOINTMENT_TYPE_STATUS_FILTER}
                onRowClick={isUnifyStaff ? openEditServiceCodeModal : undefined}
                tableProps={{ highlightOnHover: true }}
              />
            </Stack>
        ) : timezoneLoading ? (
          <Center py="xl">
            <Loader size="lg" />
          </Center>
        ) : (
          <Paper withBorder p="lg">
              <Stack gap="md">
                <div>
                  <Text fw={600} size="sm" mb="xs">
                    Timezone
                  </Text>
                  <Select
                    leftSection={<IconWorld size={16} />}
                    value={timezone}
                    placeholder="Use browser timezone"
                    clearable={false}
                    disabled={timezoneSaving}
                    onChange={(v) => {
                      if (!v) return;
                      void saveTimezone(v);
                    }}
                    data={[...CALENDAR_TIMEZONE_OPTIONS]}
                  />
                </div>
                <Divider />
                <div>
                  <Group gap={6} mb="xs">
                    <Text fw={600} size="sm">
                      Calendar display hours
                    </Text>
                    <Tooltip label="The visible time range shown on your calendar. Hours outside this window will be hidden." withArrow>
                      <IconInfoCircle size={16} style={{ color: 'var(--mantine-color-gray-5)', cursor: 'help' }} />
                    </Tooltip>
                  </Group>
                  <Group grow>
                    <Select
                      label="Start time"
                      leftSection={<IconClock size={16} />}
                      data={ALL_HOUR_OPTIONS}
                      value={displayStartHour}
                      onChange={(v) => {
                        if (!v) return;
                        setDisplayStartHour(v);
                        if (Number(v) >= Number(displayEndHour)) setDisplayEndHour(String(Math.min(Number(v) + 1, 23)));
                      }}
                    />
                    <Select
                      label="End time"
                      leftSection={<IconClock size={16} />}
                      data={ALL_HOUR_OPTIONS}
                      value={displayEndHour}
                      onChange={(v) => {
                        if (!v) return;
                        setDisplayEndHour(v);
                        if (Number(v) <= Number(displayStartHour)) setDisplayStartHour(String(Math.max(Number(v) - 1, 0)));
                      }}
                    />
                  </Group>
                </div>
              </Stack>
            </Paper>
        )}

          <Modal
            opened={sessionTypeModalOpened}
            onClose={() => { setSessionTypeModalOpened(false); setPromptEditorOpen(false); }}
            title={sessionTypeModalMode === 'edit' ? 'Edit session type' : 'Add session type'}
            centered
            size={promptEditorOpen ? '1200px' : sessionTypeModalMode === 'edit' ? 'lg' : 'md'}
            styles={{ body: { padding: 0 } }}
          >
            <Flex gap={0} align="stretch" style={{ minHeight: 0 }}>
              {/* ── Main form column ── */}
              <Box p="md" style={{ flex: '0 0 auto', width: promptEditorOpen ? 460 : '100%', minWidth: 320 }}>
                <Stack gap="md">
                  <TextInput
                    label="Description"
                    placeholder="Enter session type description"
                    value={sessionTypeForm.description}
                    onChange={(event) => setSessionTypeForm((prev) => ({ ...prev, description: event.currentTarget.value }))}
                    withAsterisk
                  />
                  <Select
                    label="Default duration"
                    placeholder="Select duration"
                    value={sessionTypeForm.durationMinutes}
                    onChange={(value) => setSessionTypeForm((prev) => ({ ...prev, durationMinutes: value ?? prev.durationMinutes }))}
                    data={SESSION_DURATION_PRESETS.map((duration) => ({ value: String(duration), label: `${duration} min` }))}
                    withAsterisk
                  />
                  <Select
                    label="Default CPT code"
                    description="Pick an active service code, or leave blank for a nonbillable session type."
                    placeholder="Select a service code"
                    data={serviceCodeSelectOptions}
                    value={sessionTypeForm.chargeItemDefinitionId}
                    onChange={(value) => {
                      const selected = chargeItemDefinitions.find((row) => row.id === value);
                      setSessionTypeForm((prev) => ({
                        ...prev,
                        chargeItemDefinitionId: selected?.id ?? null,
                        cptCode: selected?.code ?? '',
                        billingTitle: selected?.title ?? '',
                        codeSystem: selected?.codeSystem,
                        rate: selected?.rate ?? 0,
                      }));
                    }}
                    searchable
                    clearable
                  />
                  {sessionTypeForm.chargeItemDefinitionId && (
                    <Text size="xs" c="dimmed">
                      {sessionTypeForm.billingTitle || sessionTypeForm.cptCode} · ${sessionTypeForm.rate} — edit
                      the title or rate under Service Codes.
                    </Text>
                  )}
                  <Group grow>
                    <Select
                      label="Modality"
                      value={sessionTypeForm.modality}
                      onChange={(value) =>
                        setSessionTypeForm((prev) => ({
                          ...prev,
                          modality: (value as AppointmentSessionType['modality'] | null) ?? 'either',
                        }))
                      }
                      data={[
                        { value: 'either', label: 'In-person or telehealth' },
                        { value: 'in-person', label: 'In-person only' },
                        { value: 'telehealth', label: 'Telehealth only' },
                        { value: 'hybrid', label: 'Hybrid' },
                      ]}
                      withAsterisk
                    />
                  </Group>

                  <Group align="flex-end" gap="xs">
                    <Select
                      label="Note template"
                      placeholder="Select a note template"
                      data={noteTemplateOptions}
                      value={sessionTypeForm.noteTemplateId}
                      onChange={(value) =>
                        setSessionTypeForm((prev) => ({
                          ...prev,
                          noteTemplateId: value,
                          // Clear exemplar selections when template changes
                          exemplarNoteQrIds: [],
                        }))
                      }
                      searchable
                      clearable
                      withAsterisk
                      style={{ flex: 1 }}
                    />
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="lg"
                      aria-label="Preview note template"
                      disabled={!sessionTypeForm.noteTemplateId}
                      onClick={() => {
                        const tpl = noteTemplates.find((t) => t.id === sessionTypeForm.noteTemplateId);
                        if (tpl) setPreviewTemplate(tpl);
                      }}
                    >
                      <IconEye size={18} />
                    </ActionIcon>
                  </Group>

                  {/* ── Exemplar Notes ── */}
                  <Stack gap={4}>
                    <Text size="sm" fw={500}>Exemplar Notes</Text>
                    {!sessionTypeForm.noteTemplateId ? (
                      <Text size="xs" c="dimmed">Select a default note template to view exemplar notes.</Text>
                    ) : exemplarQrLoading ? (
                      <Group gap="xs">
                        <Loader size="xs" />
                        <Text size="xs" c="dimmed">Loading exemplar notes…</Text>
                      </Group>
                    ) : exemplarQrOptions.length === 0 ? (
                      <Text size="xs" c="dimmed">No exemplar notes found for this note template.</Text>
                    ) : (
                      <MultiSelect
                        placeholder="Select exemplar notes"
                        data={exemplarQrOptions}
                        value={sessionTypeForm.exemplarNoteQrIds.filter((id) =>
                          exemplarQrOptions.some((o) => o.value === id)
                        )}
                        onChange={(values) => setSessionTypeForm((prev) => ({ ...prev, exemplarNoteQrIds: values }))}
                        searchable
                        clearable
                      />
                    )}
                  </Stack>

                  {/* ── AI Note Generation Prompt button ── */}
                  {canEditAiPrompt && (
                    <Group justify="flex-start">
                      <Button
                        variant="subtle"
                        color="gray"
                        size="xs"
                        leftSection={<IconEdit size={14} />}
                        onClick={() => setPromptEditorOpen((o) => !o)}
                      >
                        {promptEditorOpen ? 'Hide AI prompt editor' : 'Edit AI Note Generation Prompt'}
                      </Button>
                    </Group>
                  )}

                  <Switch
                    label="Active"
                    checked={sessionTypeForm.active}
                    onChange={(event) => setSessionTypeForm((prev) => ({ ...prev, active: event.currentTarget.checked }))}
                    color="gold"
                  />
                  {sessionTypeModalMode === 'edit' && isUnifyStaff && sessionTypeForm.id ? (
                    <>
                      <Divider />
                      <div>
                        <Text fw={600} mb="xs">Manage access</Text>
                        <Text size="sm" c="dimmed" mb="sm">
                          Choose which staff can book this session type.
                        </Text>
                        <AppointmentTypeAccessPanel serviceId={sessionTypeForm.id} />
                      </div>
                    </>
                  ) : null}
                  <Group justify="flex-end" mt="sm">
                    <Button variant="outline" color="gray" onClick={() => { setSessionTypeModalOpened(false); setPromptEditorOpen(false); }}>
                      Cancel
                    </Button>
                    <Button
                      color="gold"
                      onClick={handleSaveSessionType}
                      disabled={
                        sessionTypeForm.description.trim().length === 0 ||
                        Number(sessionTypeForm.durationMinutes) <= 0
                      }
                    >
                      {sessionTypeModalMode === 'edit' ? 'Save changes' : 'Add session type'}
                    </Button>
                  </Group>
                </Stack>
              </Box>

              {/* ── AI Prompt editor panel ── */}
              {canEditAiPrompt && promptEditorOpen && (
                <Box
                  p="md"
                  style={{
                    flex: 1,
                    borderLeft: '1px solid var(--mantine-color-gray-3)',
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 320,
                    maxWidth: 680,
                  }}
                >
                  <Group justify="space-between" mb="xs">
                    <Stack gap={2}>
                      <Text size="sm" fw={600}>AI Note Generation Prompt</Text>
                      <Text size="xs" c="dimmed">
                        This prompt is used when generating AI notes for this service type.
                      </Text>
                    </Stack>
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="sm"
                      aria-label="Close prompt editor"
                      onClick={() => setPromptEditorOpen(false)}
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  </Group>
                  <Textarea
                    value={sessionTypeForm.aiNoteGenerationPrompt}
                    onChange={(event) =>
                      setSessionTypeForm((prev) => ({ ...prev, aiNoteGenerationPrompt: event.currentTarget.value }))
                    }
                    minRows={18}
                    autosize
                    styles={{
                      root: { flex: 1 },
                      input: { fontFamily: 'monospace', fontSize: 12, resize: 'vertical' },
                    }}
                  />
                  <Group justify="flex-end" mt="xs">
                    <Button
                      variant="subtle"
                      color="gray"
                      size="xs"
                      leftSection={<IconRotate size={14} />}
                      onClick={() =>
                        setSessionTypeForm((prev) => ({
                          ...prev,
                          aiNoteGenerationPrompt: DEFAULT_AI_NOTE_GENERATION_SYSTEM_PROMPT,
                        }))
                      }
                    >
                      Reset to default
                    </Button>
                  </Group>
                </Box>
              )}
            </Flex>
          </Modal>

          <Modal
            opened={meetingTypeModalOpened}
            onClose={() => setMeetingTypeModalOpened(false)}
            title={meetingTypeModalMode === 'edit' ? 'Edit meeting type' : 'Add meeting type'}
            centered
            size={meetingTypeModalMode === 'edit' ? 'lg' : 'md'}
          >
            <Stack gap="md">
              <TextInput
                label="Description"
                placeholder="Enter meeting type description"
                value={meetingTypeForm.description}
                onChange={(event) =>
                  setMeetingTypeForm((prev) => ({ ...prev, description: event.currentTarget.value }))
                }
                withAsterisk
              />
              <NumberInput
                label="Default duration"
                description="Minutes blocked on the calendar by default"
                value={Number(meetingTypeForm.defaultDurationMinutes)}
                onChange={(value) =>
                  setMeetingTypeForm((prev) => ({
                    ...prev,
                    defaultDurationMinutes: String(value ?? prev.defaultDurationMinutes),
                  }))
                }
                min={1}
                max={1440}
                suffix=" min"
                withAsterisk
              />
              <Switch
                label="Active"
                checked={meetingTypeForm.active}
                onChange={(event) =>
                  setMeetingTypeForm((prev) => ({ ...prev, active: event.currentTarget.checked }))
                }
                color="gold"
              />
              {meetingTypeModalMode === 'edit' && isUnifyStaff && meetingTypeForm.id ? (
                <>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">Manage access</Text>
                    <AppointmentTypeAccessPanel serviceId={meetingTypeForm.id} />
                  </div>
                </>
              ) : null}
              <Group justify="flex-end" mt="sm">
                <Button variant="outline" color="gray" onClick={() => setMeetingTypeModalOpened(false)}>
                  Cancel
                </Button>
                <Button
                  color="gold"
                  onClick={handleSaveMeetingType}
                  disabled={
                    meetingTypeForm.description.trim().length === 0 ||
                    Number(meetingTypeForm.defaultDurationMinutes) <= 0
                  }
                >
                  {meetingTypeModalMode === 'edit' ? 'Save changes' : 'Add meeting type'}
                </Button>
              </Group>
            </Stack>
          </Modal>

          <Modal
            opened={timeEntryTypeModalOpened}
            onClose={() => setTimeEntryTypeModalOpened(false)}
            title={timeEntryTypeModalMode === 'edit' ? 'Edit time entry type' : 'Add time entry type'}
            centered
            size={timeEntryTypeModalMode === 'edit' ? 'lg' : 'md'}
          >
            <Stack gap="md">
              <TextInput
                label="Description"
                placeholder="Enter time entry type description"
                value={timeEntryTypeForm.description}
                onChange={(event) =>
                  setTimeEntryTypeForm((prev) => ({ ...prev, description: event.currentTarget.value }))
                }
                withAsterisk
              />
              <NumberInput
                label="Default duration"
                description="Minutes blocked on the calendar by default"
                value={Number(timeEntryTypeForm.defaultDurationMinutes)}
                onChange={(value) =>
                  setTimeEntryTypeForm((prev) => ({
                    ...prev,
                    defaultDurationMinutes: String(value ?? prev.defaultDurationMinutes),
                  }))
                }
                min={1}
                max={1440}
                suffix=" min"
                withAsterisk
              />
              <Switch
                label="Active"
                checked={timeEntryTypeForm.active}
                onChange={(event) =>
                  setTimeEntryTypeForm((prev) => ({ ...prev, active: event.currentTarget.checked }))
                }
                color="gold"
              />
              {timeEntryTypeModalMode === 'edit' && isUnifyStaff && timeEntryTypeForm.id ? (
                <>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">Manage access</Text>
                    <AppointmentTypeAccessPanel serviceId={timeEntryTypeForm.id} />
                  </div>
                </>
              ) : null}
              <Group justify="flex-end" mt="sm">
                <Button variant="outline" color="gray" onClick={() => setTimeEntryTypeModalOpened(false)}>
                  Cancel
                </Button>
                <Button
                  color="gold"
                  onClick={handleSaveTimeEntryType}
                  disabled={
                    timeEntryTypeForm.description.trim().length === 0 ||
                    Number(timeEntryTypeForm.defaultDurationMinutes) <= 0
                  }
                >
                  {timeEntryTypeModalMode === 'edit' ? 'Save changes' : 'Add time entry type'}
                </Button>
              </Group>
            </Stack>
          </Modal>

          <Modal
            opened={serviceCodeModalOpened}
            onClose={() => setServiceCodeModalOpened(false)}
            title={serviceCodeModalMode === 'edit' ? 'Edit service code' : 'Add service code'}
            centered
          >
            <Stack gap="md">
              <ProcedureCodeSelect
                code={serviceCodeForm.code}
                title={serviceCodeForm.title}
                onSelect={(concept) =>
                  setServiceCodeForm((prev) => ({
                    ...prev,
                    code: concept.code,
                    codeSystem: concept.system,
                    title: serviceCodeModalMode === 'add' || prev.code !== concept.code
                      ? concept.display
                      : prev.title,
                  }))
                }
                required
              />
              <TextInput
                label="Title"
                placeholder="Official CPT or HCPCS descriptor"
                value={serviceCodeForm.title}
                onChange={(event) =>
                  setServiceCodeForm((prev) => ({ ...prev, title: event.currentTarget.value }))
                }
                withAsterisk
              />
              <NumberInput
                label="Default rate"
                value={serviceCodeForm.rate}
                onChange={(value) =>
                  setServiceCodeForm((prev) => ({ ...prev, rate: Number(value ?? 0) }))
                }
                min={0}
                prefix="$"
                decimalScale={2}
              />
              <Switch
                label="Active"
                checked={serviceCodeForm.active}
                onChange={(event) =>
                  setServiceCodeForm((prev) => ({ ...prev, active: event.currentTarget.checked }))
                }
                color="gold"
              />
              <Group justify="flex-end" mt="sm">
                <Button variant="outline" color="gray" onClick={() => setServiceCodeModalOpened(false)}>
                  Cancel
                </Button>
                <Button
                  color="gold"
                  onClick={() => void handleSaveServiceCode()}
                  disabled={
                    serviceCodeForm.title.trim().length === 0
                    || serviceCodeForm.code.trim().length === 0
                  }
                >
                  {serviceCodeModalMode === 'edit' ? 'Save changes' : 'Add service code'}
                </Button>
              </Group>
            </Stack>
          </Modal>

          <TemplatePreviewDrawer
            opened={previewTemplate !== null}
            onClose={() => setPreviewTemplate(null)}
            template={previewTemplate}
          />
      </Stack>
    </Container>
  );
}

function BillingSettings(): JSX.Element {
  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        <div>
          <Group gap="sm" align="center" wrap="wrap" mb="xs">
            <Title order={2}>
              Billing & payments
            </Title>
            <LocalhostOnlyBadge />
          </Group>
          <Text size="sm" c="dimmed">
            Practice billing for clients lives on each client chart. This page does not store a card for your staff login.
          </Text>
        </div>

        <Paper p="xl" withBorder>
          <Stack gap="xs">
            <Text fw={600}>Payment methods</Text>
            <Text size="sm" c="dimmed">
              No payment method is on file for this account. Client cards are added from the client&apos;s Billing tab.
            </Text>
          </Stack>
        </Paper>
      </Stack>
    </Container>
  );
}

function PracticesUsersSettings(): JSX.Element {
  return <UsersSectionPage />;
}

function DocumentationSettings(): JSX.Element {
  return <TemplateLibrary />;
}

function EmailNotificationsSettings(): JSX.Element {
  return <NotificationTemplatesPage />;
}

function ReportsSettings(): JSX.Element {
  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        <div>
          <Group gap="sm" align="center" wrap="wrap" mb="xs">
            <Title order={2} mb={0}>
              Reports & analytics
            </Title>
            <LocalhostOnlyBadge />
          </Group>
          <Text size="sm" c="dimmed">
            Configure reporting and analytics preferences
          </Text>
        </div>

        <Paper p="xl" withBorder>
          <Text size="sm" c="dimmed">
            Reports configuration coming soon…
          </Text>
        </Paper>
      </Stack>
    </Container>
  );
}

function IntegrationsSettings({ personal = false }: { personal?: boolean }): JSX.Element {
  const medplum = useMedplum();
  const { practitioner, loading: practitionerLoading } = useCurrentUserPractitioner();
  const [providerPractitioner, setProviderPractitioner] = useState<Practitioner | null>(null);
  const [testingEmail, setTestingEmail] = useState(false);

  useEffect(() => {
    setProviderPractitioner(practitioner);
  }, [practitioner]);

  const handleTestEmail = async (): Promise<void> => {
    setTestingEmail(true);
    try {
      const result = await sendSystemStatusTestEmail(medplum);
      if (result.queuedCount > 0 && result.failedCount === 0) {
        notifications.show({
          color: 'green',
          title: 'Test email queued',
          message:
            `Queued ${result.queuedCount} test email${result.queuedCount === 1 ? '' : 's'} ` +
            'to the operations inboxes. Queueing is not delivery: locally check Mailpit ' +
            '(http://localhost:8025); hosted Medplum sends via SES to those inboxes.',
        });
        return;
      }
      if (result.queuedCount > 0 && result.failedCount > 0) {
        notifications.show({
          color: 'yellow',
          title: 'Partial queue',
          message:
            `Queued ${result.queuedCount}, failed ${result.failedCount}. ` +
            'Queued mail is still delivered asynchronously.',
        });
        return;
      }
      notifications.show({
        color: 'red',
        title: 'Test email failed',
        message: 'Could not queue test emails.',
      });
    } catch {
      notifications.show({
        color: 'red',
        title: 'Test email failed',
        message: 'Could not queue test emails.',
      });
    } finally {
      setTestingEmail(false);
    }
  };

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        <div>
          <Title order={2} mb="xs">
            {personal ? 'My Integrations' : 'System Integrations'}
          </Title>
          <Text size="sm" c="dimmed">
            {personal
              ? 'Manage your connected services and AI documentation preferences'
              : 'Test outbound connections. Email Test queues mail to the operations inboxes (Mailpit locally; SES on hosted Medplum).'}
          </Text>
        </div>

        {personal && providerPractitioner && (
          <AiDocumentationOptOutCard
            practitioner={providerPractitioner}
            onUpdated={setProviderPractitioner}
          />
        )}

        {personal && practitionerLoading && !providerPractitioner && (
          <Paper p="xl" withBorder>
            <Group justify="center">
              <Loader size="sm" />
              <Text size="sm" c="dimmed">Loading your settings…</Text>
            </Group>
          </Paper>
        )}

        <Paper p="xl" withBorder>
          <Stack gap="lg">
            <Group justify="space-between">
              <Group>
                <IconMail size={24} />
                <div>
                  <Text fw={600}>
                    Email provider
                  </Text>
                  <Text size="sm" c="dimmed">
                    {personal
                      ? 'Email service connection will be available soon.'
                      : 'Send a test email through the configured email service'}
                  </Text>
                </div>
              </Group>
              <Button
                variant="outline"
                color="gray"
                onClick={personal ? undefined : () => void handleTestEmail()}
                loading={!personal && testingEmail}
                disabled={personal || testingEmail}
              >
                {personal ? 'Coming soon' : 'Test'}
              </Button>
            </Group>
            {personal && (
              <>
                <Divider />
                <Group justify="space-between">
                  <Group>
                    <IconCalendar size={24} />
                    <div>
                      <Text fw={600}>
                        Google Calendar
                      </Text>
                      <Text size="sm" c="dimmed">
                        Google Calendar sync will be available soon.
                      </Text>
                    </div>
                  </Group>
                  <Button variant="outline" color="gray" disabled>
                    Coming soon
                  </Button>
                </Group>
              </>
            )}
          </Stack>
        </Paper>
        {!personal ? (
          <Paper p="xl" withBorder>
            <ZoomLicenseStatusCard />
          </Paper>
        ) : null}
      </Stack>
    </Container>
  );
}

